import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';



import { useAuth } from '../utils/AuthContext';
import { useNavigate } from 'react-router-dom';
import InterviewManagement from '../components/interview/InterviewManagement';
import { adminAPI } from '../services/api';
import CandidateSearch from '../components/admin/CandidateSearch';
import CandidateReviews from '../components/admin/CandidateReviews';
import InterviewSearch from '../components/admin/InterviewSearch';
import { API_PREFIX } from '../services/api';
// 🚀 PERFORMANCE IMPORTS
import { ActionLoader, LoadableTableRow, JobCardSkeleton } from '../components/common/LoadingComponents';
import { useEfficientAPI, useDebounce } from '../hooks/useEfficientAPI';
// Update your existing Material-UI imports (around line 1-30)

import {
  Box,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
  GridLegacy as Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Divider,
  Autocomplete,
  Checkbox,
  Switch,
  FormGroup,
  FormControlLabel,
  FormLabel,
  Stack,
  Tooltip,
  Badge,
  Fab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,

  FormHelperText,

  useTheme,
  useMediaQuery
} from '@mui/material';

// ✅ FIXED: Complete Icons import
import {
  AdminPanelSettings,
  TrendingUp,
  Work,
  People,
  Assessment,
  Add,
  Edit,
  Delete,
  Block,
  Schedule,
  Assignment,
  ManageAccounts,
  BusinessCenter,
  SupervisorAccount,
  RateReview,
  Refresh,
  Save,
  MoreVert,
  CheckCircle,
  RemoveCircle,
  Repeat,
  Today,
  AccessTime,
  VideoCall,
  Comment as FeedbackIcon,
  AssignmentTurnedIn,
  Cancel as EventBusyIcon,
  CalendarMonth as CalendarIcon,
  FilterList,
  Download,
  Close,
  Warning,
  Logout,
  AccountCircle,
  Security,
  ArrowBack,
  ArrowForward,
  Search,
  Clear
} from '@mui/icons-material';


// ADDED: Enhanced imports for slot management
import TalentFlowCalendar from '../components/shared/TalentFlowCalendar';
import PanelistAvailabilityManagement from '../components/admin/PanelistAvailabilityManagement';
import AppShell from '../components/shared/AppShell';
import {
  Section,
  SectionHeader,
  EmptyState,
  StatusChip,
  StatCard as UiStatCard,
} from '../components/shared/ui';
import { tokens } from '../theme';

// ✅ NEW: Import for the Job Tracking Pipeline
import JobTrackingPipeline from '../components/admin/JobTrackingPipeline';

/**
 * Whether an account has actually been blocked by an administrator.
 *
 * Deliberately an explicit `=== false` rather than `!user.is_active`. The
 * previous falsy test meant a user whose is_active arrived as null or undefined
 * was listed as blocked -- which is what happened to newly created users,
 * because `users.is_active` was nullable with no server-side default, so any
 * insert that did not name the column stored NULL. The account worked fine; it
 * just looked revoked to whoever was administering it.
 *
 * migrations_sql/007 makes the column NOT NULL DEFAULT TRUE so the ambiguous
 * state cannot be stored again. This keeps the UI honest even so: a missing
 * field means "unknown", and unknown is not a reason to tell an administrator
 * that someone is blocked. A real block is written as an explicit false.
 */
const isBlocked = (user) => user?.is_active === false;

/**
 * One row in a role table (vendors, panelists, hiring managers).
 *
 * Declared at module scope, and that matters rather than being a style choice.
 * It used to be defined inside AdminDashboard, so every render produced a new
 * function identity; React treats that as a different component type and
 * unmounts the whole subtree instead of updating it. Clicking the row's action
 * button sets state, which re-renders, which destroyed the very button the
 * action menu had just been anchored to -- and MUI cannot position against a
 * detached node, so the menu appeared at the top-left corner of the window
 * instead of beside the button.
 */
const UserRow = ({ user, accent, secondary, countIcon, count, children }) => (
  <TableRow hover key={user.id}>
    <TableCell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: accent }}>
          {user.username?.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 550 }} noWrap>
            {user.username}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.color.inkTertiary }} noWrap>
            {user.email}
          </Typography>
        </Box>
      </Box>
    </TableCell>
    <TableCell sx={{ color: tokens.color.inkSecondary }}>{secondary || '—'}</TableCell>
    <TableCell>
      <StatusChip status={isBlocked(user) ? 'blocked' : 'active'} />
    </TableCell>
    <TableCell align="right">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75,
                 color: tokens.color.inkSecondary }}>
        {countIcon}
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {count || 0}
        </Typography>
      </Box>
    </TableCell>
    <TableCell align="right">{children}</TableCell>
  </TableRow>
);



// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}





// Time Picker Component (preserved your existing component)
const TimePickerButton = ({ label, value, onChange, helperText, fullWidth = true }) => {
  const [selectedTime, setSelectedTime] = useState(value || '');

  const timeOptions = [];
  for (let hour = 6; hour <= 23; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const displayTime = new Date(`2000-01-01 ${timeStr}`).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      timeOptions.push({ value: timeStr, label: displayTime });
    }
  }

  useEffect(() => {
    setSelectedTime(value || '');
  }, [value]);

  const handleTimeChange = (newTime) => {
    setSelectedTime(newTime);
    if (onChange) {
      onChange(newTime);
    }
  };

  return (
    <FormControl fullWidth={fullWidth}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={selectedTime}
        onChange={(e) => handleTimeChange(e.target.value)}
        label={label}
        startAdornment={<AccessTime sx={{ mr: 1, color: 'action.active' }} />}
      >
        {timeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTime fontSize="small" color="primary" />
              {option.label}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

// ✅ Add this RIGHT BEFORE your main AdminDashboard function
const PriorityDisplay = ({ priority, subPriority, deadline, isDeadlinePassed }) => {
    const getPriorityColor = (p) => {
        switch(p) {
            case 'urgent': return '#d32f2f';
            case 'high': return '#f44336';
            case 'medium': return '#ff9800';
            case 'low': return '#4caf50';
            default: return '#757575';
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                    label={priority}
                    size="small"
                    sx={{
                        bgcolor: getPriorityColor(priority),
                        color: 'white',
                        fontWeight: 600,
                        textTransform: 'uppercase'
                    }}
                />
                {subPriority && (
                    <Chip
                        label={subPriority.toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderColor: getPriorityColor(priority),
                            color: getPriorityColor(priority),
                            fontWeight: 600
                        }}
                    />
                )}
            </Box>
            {deadline && (
                <Typography variant="caption" sx={{
                    color: isDeadlinePassed ? 'error.main' : 'text.secondary',
                    fontWeight: isDeadlinePassed ? 600 : 400,
                    fontSize: '0.65rem'
                }}>
                    {isDeadlinePassed ? '⚠️ EXPIRED' : `Due: ${new Date(deadline).toLocaleDateString()}`}
                </Typography>
            )}
        </Box>
    );
};

// ✅ ADD THESE HELPER FUNCTIONS RIGHT BEFORE: const AdminDashboard = () => {

// Enhanced date formatting with timestamp
const formatCreatedDate = (dateString) => {
  if (!dateString) return { date: 'N/A', time: '' };
  try {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short'
      }),
      time: date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  } catch {
    return { date: 'N/A', time: '' };
  }
};

// NEW: Enhanced budget formatting with currency support
const formatBudget = (job) => {
  // First try to use formatted_budget from backend
  if (job.formatted_budget && job.formatted_budget !== 'Not specified') {
    return job.formatted_budget;
  }

  // If we have structured budget fields, format them
  if (job.budget_min || job.budget_max) {
    const currencySymbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'INR': '₹',
      'CAD': 'C$', 'AUD': 'A$', 'CHF': 'CHF', 'CNY': '¥', 'SGD': 'S$'
    };
    const symbol = currencySymbols[job.budget_currency] || job.budget_currency;

    if (job.budget_min && job.budget_max) {
      const minFormatted = `${symbol}${parseInt(job.budget_min).toLocaleString()}`;
      const maxFormatted = `${symbol}${parseInt(job.budget_max).toLocaleString()}`;
      return `${minFormatted} - ${maxFormatted}`;
    } else if (job.budget_max) {
      return `Up to ${symbol}${parseInt(job.budget_max).toLocaleString()}`;
    } else if (job.budget_min) {
      return `From ${symbol}${parseInt(job.budget_min).toLocaleString()}`;
    }
  }

  // Fallback to legacy salary_range
  return job.salary_range || 'Not specified';
};

// NEW: Budget display with currency indicator
const getBudgetDisplay = (job) => {
  const budgetText = formatBudget(job);
  const currency = job.budget_currency || 'USD';

  return {
    text: budgetText,
    currency: currency,
    isStructured: !!(job.budget_min || job.budget_max),
    isLegacy: !!(job.salary_range && !job.budget_min && !job.budget_max)
  };
};

// Compact job code display
const getJobDisplayCode = (job) => {
  if (job.job_code && job.job_code.trim()) {
    return job.job_code;
  }
  return `JOB-${job.id}`;
};

// Enhanced status colors for better visibility
const getStatusColor = (status) => {
  switch(status) {
    case 'open': return '#2e7d32';
    case 'closed': return '#d32f2f';
    case 'filled': return '#1565c0';
    case 'on_hold': return '#ef6c00';
    case 'draft': return '#5e35b1';
    default: return '#424242';
  }
};
// ✅ ADD THIS CUSTOM HOOK to fix cursor jumping
const usePreserveCursor = () => {
  const ref = useRef(null);
  const cursorPosition = useRef(null);

  useEffect(() => {
    const input = ref.current;
    if (input && cursorPosition.current !== null) {
      input.setSelectionRange(cursorPosition.current, cursorPosition.current);
    }
  });

  const handleChange = (e, callback) => {
    cursorPosition.current = e.target.selectionStart;
    callback(e);
  };

  return [ref, handleChange];
};




const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, isAuthenticated, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    analytics: null,
    jobs: [],
    vendors: [],
    panelists: [],
    managers: [],
    candidates: [],
    slots: [],
    interviews: [],
    loading: false,
    error: null,
    lastLoaded: null,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });




  // Loop prevention refs
  const loadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const mountedRef = useRef(false);

  // Dialog states (preserved all your existing states)
  const [jobDialog, setJobDialog] = useState(false);
  const [editJobDialog, setEditJobDialog] = useState(false);
  // Add these new state variables
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsPerPage] = useState(12);
  const [jobsStatusFilter, setJobsStatusFilter] = useState('all');

  // ✅ ADD THESE NEW PRIORITY FILTERING STATE VARIABLES HERE:
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [subPriorityFilter, setSubPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('priority_desc');
  const [priorityOptions, setPriorityOptions] = useState([]);


  // In AdminDashboard.js - Add this state for currencies
const [supportedCurrencies, setSupportedCurrencies] = useState([
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' }
]);

  const [assignmentDialog, setAssignmentDialog] = useState(false);
  const [userDialog, setUserDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [slotDialog, setSlotDialog] = useState(false);
  const [advancedSlotDialog, setAdvancedSlotDialog] = useState(false);
  const [editSlotDialog, setEditSlotDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', action: null });

  // ADDED: New slot management states
  const [selectedPanelist, setSelectedPanelist] = useState('');
  const [allSlots, setAllSlots] = useState([]);
  const [slotManagementLoading, setSlotManagementLoading] = useState(false);
  const [slotFilters, setSlotFilters] = useState({
    status: 'all',
    panelist: 'all',
    dateRange: { start: null, end: null }
  });
  const [teamCalendarDialog, setTeamCalendarDialog] = useState(false);

  // Menu states (preserved)
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const menuOpen = Boolean(anchorEl && selectedUser);
  const [jobSearchTerm, setJobSearchTerm] = useState('');

  // ✅ ADD THESE TWO LINES for the assignment tooltip feature
  const [assignmentTooltips, setAssignmentTooltips] = useState({});
  const [hoveredJob, setHoveredJob] = useState(null);

  // 🚀 PERFORMANCE: Individual loading states for better UX
  const [itemLoadingStates, setItemLoadingStates] = useState({});
  const [operationStates, setOperationStates] = useState({}); // success/error states

  // 🚀 PERFORMANCE: Efficient API and caching
  const { cachedFetch, debouncedFetch, clearCache } = useEfficientAPI();
  const debouncedSearch = useDebounce((term) => {
    // Debounced search logic here if needed
  }, 300);

  // 🚀 PERFORMANCE: Helper functions for loading states
  const setItemLoading = useCallback((itemId, isLoading) => {
    setItemLoadingStates(prev => ({
      ...prev,
      [itemId]: isLoading
    }));
  }, []);

  const setOperationState = useCallback((itemId, state, duration = 2000) => {
    setOperationStates(prev => ({
      ...prev,
      [itemId]: state
    }));

    if (state === 'success' || state === 'error') {
      setTimeout(() => {
        setOperationStates(prev => ({
          ...prev,
          [itemId]: null
        }));
      }, duration);
    }
  }, []);

  // 🚀 PERFORMANCE: Request deduplication
  const pendingRequests = useRef(new Map());
  const makeUniqueRequest = useCallback(async (key, requestFn) => {
    if (pendingRequests.current.has(key)) {
      return pendingRequests.current.get(key);
    }

    const promise = requestFn().finally(() => {
      pendingRequests.current.delete(key);
    });

    pendingRequests.current.set(key, promise);
    return promise;
  }, []);

  // Left nav expand-on-hover

  // 🔽 NEW: State for Role Management
  const [roleAssignments, setRoleAssignments] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    email: '',
    full_name: '',
    role: 'panelist',
    auth_method: 'microsoft_sso',
    department: '',
    company_name: '',
    user_type: 'internal'
  });


const [newJob, setNewJob] = useState({
   title: '',
  description: '',
  requirements: '',
  location: '',
  salary_range: '', // Keep for backward compatibility
  budget_min: '',
  budget_max: '',
  budget_currency: 'USD',
  status: 'open',
  priority: 'medium',
  sub_priority: '',  // ✅ ADD THIS LINE
  hiring_urgency: 'standard',
  expected_start_date: '',
  deadline: '',      // ✅ ADD THIS LINE
  is_readonly: false,
  expected_hires: 1
});


// Add currency formatting helper
const formatCurrency = (amount, currency) => {
  if (!amount) return '';
  const currencyInfo = supportedCurrencies.find(c => c.code === currency);
  const symbol = currencyInfo?.symbol || currency;
  return `${symbol} ${parseFloat(amount).toLocaleString()}`;
};

// ✅ Add this function after your other functions
const loadPriorityOptions = useCallback(async () => {
    try {
        const response = await fetch(`${API_PREFIX}/admin/priority-options`, {
            credentials: 'include'
        });
        if (response.ok) {
            const data = await response.json();
            setPriorityOptions(data.options);
        }
    } catch (error) {
        console.error('Failed to load priority options:', error);
    }
}, []);

// ✅ Add this useEffect after your existing useEffect hooks
useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
        loadPriorityOptions();
    }
}, [isAuthenticated, user?.role, loadPriorityOptions]);



const [editJob, setEditJob] = useState({
  id: '',
  title: '',
  description: '',
  requirements: '',
  location: '',
  salary_range: '',
  hiring_urgency: 'standard',
  expected_start_date: '',
  // ✅ ADD THESE CURRENCY FIELDS
  budget_min: '',
  budget_max: '',
  budget_currency: 'USD',
  priority: 'medium',
  status: 'open'
});

  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'vendor',
    company_name: '',
    contact_person: '',
    phone: '',
    expertise: '',
    experience_years: 5,
    department: 'HR'
  });

  const [editUser, setEditUser] = useState({
    id: '',
    username: '',
    email: '',
    role: '',
    company_name: '',
    contact_person: '',
    phone: '',
    expertise: '',
    experience_years: 5,
    department: 'HR'
  });

  const [newSlot, setNewSlot] = useState({
    panelist_id: '',
    date: '',
    start_time: '',
    end_time: '',
    notes: '',
    slot_type: 'interview'
  });

  const [advancedSlot, setAdvancedSlot] = useState({
    panelist_id: '',
    date: '',
    start_time: '',
    end_time: '',
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    recurrence_end_date: '',
    notes: '',
    slot_type: 'interview'
  });

  const [assignmentData, setAssignmentData] = useState({
    jobId: null,
    selectedVendors: [],
    selectedPanelists: [],
    currentVendors: [],
    currentPanelists: []
  });

  const showSnackbar = (message, severity = 'error') => {
    setSnackbar({ open: true, message, severity });
  };

    // 🔽 NEW: Function to fetch role assignments
    const fetchRoleAssignments = useCallback(async () => {
        try {
            const response = await fetch(`${API_PREFIX}/admin/users/role-assignments`, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                if (data.status === 'success' && mountedRef.current) {
                    setRoleAssignments(data.users || []);
                }
            } else {
                 showSnackbar('Failed to fetch role assignments', 'error');
            }
        } catch (error) {
            showSnackbar(`Failed to fetch role assignments: ${error.message}`, 'error');
        }
    }, []);

    // 🔽 NEW: Function to assign a role
    const assignRole = async () => {
        try {
            const response = await fetch(`${API_PREFIX}/admin/users/assign-role`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newAssignment)
            });
            const data = await response.json();
            if (response.ok && data.status === 'success') {
                showSnackbar('Role assigned successfully', 'success');
                setShowAssignDialog(false);
                fetchRoleAssignments(); // Re-fetch the list
                // Reset form
                setNewAssignment({
                    email: '',
                    full_name: '',
                    role: 'panelist',
                    auth_method: 'microsoft_sso',
                    department: '',
                    company_name: '',
                    user_type: 'internal'
                });
            } else {
                showSnackbar(data.error || 'Failed to assign role', 'error');
            }
        } catch (error) {
            showSnackbar(`Failed to assign role: ${error.message}`, 'error');
        }
    };


  // ✅ ADD THIS FUNCTION to fetch assignment details for the tooltip
  const fetchAssignmentTooltip = useCallback(async (jobId) => {
    // If data is already in our cache, don't fetch it again
    if (assignmentTooltips[jobId]) {
      return;
    }

    try {
      const response = await fetch(`${API_PREFIX}/admin/jobs/${jobId}/assignments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Update the state with the new data, caching it
        setAssignmentTooltips(prev => ({
          ...prev,
          [jobId]: {
            vendors: data.vendors || [],
            panelists: data.panelists || []
          }
        }));
      } else {
        // If the fetch fails, cache an empty result to avoid re-fetching on every hover
        setAssignmentTooltips(prev => ({ ...prev, [jobId]: { vendors: [], panelists: [] } }));
      }
    } catch (error) {
      console.error('Failed to fetch assignment tooltip data:', error);
      // Also cache an empty result on network error
      setAssignmentTooltips(prev => ({ ...prev, [jobId]: { vendors: [], panelists: [] } }));
    }
  }, [assignmentTooltips]); // Dependency ensures the function updates if the cache changes


  // Shared tabs configuration for nav + dropdown
  const tabsConfig = [
    { label: 'Overview', icon: <Assessment /> },
    { label: 'Job Management', icon: <Work /> },
    { label: 'User Management', icon: <People /> },
    { label: 'Interview Management', icon: <VideoCall /> },
    { label: 'Slot Management', icon: <CalendarIcon /> },
    { label: 'Team Calendar', icon: <Schedule /> },
    { label: 'Job Tracking', icon: <TrendingUp /> },
    { label: 'Candidate Search', icon: <People /> },
    { label: 'CV Reviews', icon: <RateReview /> },
    { label: 'Interview Search', icon: <Assessment /> },
  ];


  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };



  // UPDATED: Centralized tab change logic to reuse for dropdown and any tabs
  const changeTabTo = (newValue) => {
    setTabValue(newValue);
  };


  // UPDATED: Tab change handler with slot management
  const TabhandleChange = (event, newValue) => {
    setTabValue(newValue);

    if (newValue === 3 && dashboardData.interviews.length === 0) {
      loadInterviews();
    }
    // ADDED: Load all slots when switching to slot management tabs
    if (newValue === 4 && allSlots.length === 0) {
      loadAllSlotsForCalendar();
    }
    if (newValue === 5) {
      loadAllSlotsForCalendar();
    }
  };


  // For any legacy Tab components still present
  const handleTabChange = (event, newValue) => {
    changeTabTo(newValue);
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  // Helper function to combine date and time properly with validation (preserved)
  const combineDateTime = (date, time) => {
    if (!date || !time) {
      console.log('â Œ Missing date or time:', { date, time });
      return '';
    }

    try {
      const dateTimeString = `${date}T${time}:00`;
      const dateTime = new Date(dateTimeString);

      if (isNaN(dateTime.getTime())) {
        console.log('â Œ Invalid datetime created:', dateTimeString);
        return '';
      }

      const isoString = dateTime.toISOString();
      console.log('âœ… Combined datetime:', { date, time, result: isoString });
      return isoString;
    } catch (error) {
      console.error('â Œ DateTime combination error:', error);
      return '';
    }
  };

  // ADDED: Enhanced slot loading function for admin overview
  // FIXED: Load slots from the correct table and API
// FIXED: Load slots from the correct table and API
const loadAllSlotsForCalendar = useCallback(async () => {
  try {
    setSlotManagementLoading(true);
    console.log('ðŸ—“ï¸  Loading all slots for admin team calendar...');

    if (!dashboardData.panelists || dashboardData.panelists.length === 0) {
      console.log('No panelists found, skipping slot loading');
      setAllSlots([]);
      return;
    }

    const activePanelists = (dashboardData.panelists || []).filter(p => p.is_active);
    const allSlotsPromises = activePanelists.map(async (panelist) => {
      try {
        console.log(`ðŸ“… Loading slots for ${panelist.username} (ID: ${panelist.id})...`);

        // Use the correct API endpoint that matches backend
        const response = await fetch(`${API_PREFIX}/admin/panelists/${panelist.id}/availability-slots`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error(`â Œ API Error for panelist ${panelist.id}:`, errorData);
          return [];
        }

        const data = await response.json();

        if (data.status === 'success' && data.slots) {
          return data.slots.map(slot => ({
            ...slot,
            panelist_name: panelist.username,
            panelist_email: panelist.email,
            role: 'panelist',
            color: slot.is_booked ? '#f44336' : slot.is_available ? '#4caf50' : '#9e9e9e'
          }));
        }
        return [];
      } catch (error) {
        console.error(`â Œ Failed to load slots for ${panelist.username}:`, error);
        return [];
      }
    });

    const slotsArrays = await Promise.all(allSlotsPromises);
    const combinedSlots = slotsArrays.flat();

    // Apply filters
    let filteredSlots = combinedSlots;
    if (slotFilters.status !== 'all') {
      filteredSlots = filteredSlots.filter(slot => {
        if (slotFilters.status === 'available') return slot.is_available && !slot.is_booked;
        if (slotFilters.status === 'booked') return slot.is_booked;
        if (slotFilters.status === 'unavailable') return !slot.is_available;
        return true;
      });
    }

    if (slotFilters.panelist !== 'all') {
      filteredSlots = filteredSlots.filter(slot => slot.panelist_id == slotFilters.panelist);
    }

    setAllSlots(filteredSlots);

    console.log(`Ã¢Å“â€¦ Loaded ${filteredSlots.length} total slots from ${activePanelists.length} active panelists`);

    console.log(`âœ… Loaded ${filteredSlots.length} total slots from ${dashboardData.panelists.length} panelists`);


  } catch (error) {
    console.error('â Œ Failed to load team calendar slots:', error);
    setAllSlots([]);
  } finally {
    setSlotManagementLoading(false);
  }
}, [dashboardData.panelists, slotFilters]);



  // Dashboard loading with loop prevention (preserved your exact implementation)
  const loadDashboardData = useCallback(async (forceReload = false) => {
    if (loadingRef.current) {
      console.log('ðŸ”„ Dashboard already loading, skipping...');
      return;
    }

    if (hasLoadedRef.current && !forceReload) {
      console.log('ðŸ”„ Dashboard already loaded, skipping...');
      return;
    }

    if (!mountedRef.current) {
      console.log('ðŸ”„ Component not mounted yet, skipping...');
      return;
    }

    console.log('ðŸ“Š Starting dashboard load...');
    loadingRef.current = true;

    if (!forceReload) {
      hasLoadedRef.current = true;
    }

    setDashboardData(prev => ({ ...prev, loading: true, error: null }));

    try {
      const [analyticsRes, jobsRes, vendorsRes, panelistsRes, managersRes, candidatesRes] =
        await Promise.allSettled([
          fetch(`${API_PREFIX}/admin/analytics`, { credentials: 'include' }),
          fetch(`${API_PREFIX}/admin/jobs?per_page=20`, { credentials: 'include' }),
          fetch(`${API_PREFIX}/admin/vendors`, { credentials: 'include' }),
          fetch(`${API_PREFIX}/admin/panelists`, { credentials: 'include' }),
          fetch(`${API_PREFIX}/admin/managers`, { credentials: 'include' }),
          fetch(`${API_PREFIX}/admin/candidates?per_page=50`, { credentials: 'include' }),
        ]);

      const getResponseData = async (result) => {
        if (result.status === 'fulfilled' && result.value.ok) {
          return await result.value.json();
        }
        return { analytics: {}, jobs: [], vendors: [], panelists: [], managers: [], candidates: [] };
      };

      const [analytics, jobs, vendors, panelists, managers, candidates] = await Promise.all([
        getResponseData(analyticsRes),
        getResponseData(jobsRes),
        getResponseData(vendorsRes),
        getResponseData(panelistsRes),
        getResponseData(managersRes),
        getResponseData(candidatesRes),
      ]);

      if (mountedRef.current) {
        setDashboardData({
          analytics: analytics.analytics || {},
          jobs: jobs.jobs || [],
          vendors: vendors.vendors || [],
          panelists: panelists.panelists || [],
          managers: managers.managers || [],
          candidates: candidates.candidates || [],
          slots: [],
          interviews: [],
          loading: false,
          error: null,
          lastLoaded: new Date().toLocaleTimeString(),
        });

        console.log('âœ… Dashboard loaded successfully');
      }

    } catch (error) {
      console.error('â Œ Dashboard load failed:', error);

      if (mountedRef.current) {
        setDashboardData(prev => ({
          ...prev,
          loading: false,
          error: `Failed to load dashboard: ${error.message}`
        }));
      }
    } finally {
      loadingRef.current = false;
    }
  }, [makeUniqueRequest]);  // 🚀 Add makeUniqueRequest dependency

  // 🚀 TARGETED UPDATE FUNCTIONS - Much faster than full refresh
  const updateAnalytics = useCallback(async () => {
    return makeUniqueRequest('analytics', async () => {
      try {
        const response = await fetch(`${API_PREFIX}/admin/analytics`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setDashboardData(prev => ({ ...prev, analytics: data.analytics }));
        }
      } catch (error) {
        console.error('Failed to update analytics:', error);
      }
    });
  }, [makeUniqueRequest]);

  const updateJobs = useCallback(async () => {
    return makeUniqueRequest('jobs', async () => {
      try {
        const response = await fetch(`${API_PREFIX}/admin/jobs?per_page=50`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setDashboardData(prev => ({ ...prev, jobs: data.jobs || [] }));
        }
      } catch (error) {
        console.error('Failed to update jobs:', error);
      }
    });
  }, [makeUniqueRequest]);

  const updateUsers = useCallback(async () => {
    return makeUniqueRequest('users', async () => {
      try {
        const [vendorsRes, panelistsRes, managersRes] = await Promise.allSettled([
          fetch(`${API_PREFIX}/admin/vendors`, { credentials: 'include' }),
          fetch(`${API_PREFIX}/admin/panelists`, { credentials: 'include' }),
          fetch(`${API_PREFIX}/admin/managers`, { credentials: 'include' }),
        ]);

        if (vendorsRes.status === 'fulfilled' && vendorsRes.value.ok) {
          const vendorData = await vendorsRes.value.json();
          setDashboardData(prev => ({ ...prev, vendors: vendorData.vendors || [] }));
        }

        if (panelistsRes.status === 'fulfilled' && panelistsRes.value.ok) {
          const panelistData = await panelistsRes.value.json();
          setDashboardData(prev => ({ ...prev, panelists: panelistData.panelists || [] }));
        }

        if (managersRes.status === 'fulfilled' && managersRes.value.ok) {
          const managerData = await managersRes.value.json();
          setDashboardData(prev => ({ ...prev, managers: managerData.managers || [] }));
        }
      } catch (error) {
        console.error('Failed to update users:', error);
      }
    });
  }, [makeUniqueRequest]);

  const handleManualRefresh = useCallback(async () => {
    console.log('ðŸ”„ Manual refresh triggered...');
    hasLoadedRef.current = false;
    await loadDashboardData(true);
    // ADDED: Also refresh slot data
    if (dashboardData.panelists.length > 0) {
      await loadAllSlotsForCalendar();
    }
    await fetchRoleAssignments(); // Refresh role assignments
  }, [loadDashboardData, dashboardData.panelists.length, loadAllSlotsForCalendar, fetchRoleAssignments]);

  // 🚀 PERFORMANCE: Smart background sync - only sync when tab is active
  const backgroundSync = useCallback(async () => {
    if (document.hidden || loadingRef.current) return;
    
    // Smart sync: only update analytics every 30 seconds, other data every 2 minutes
    const lastUpdate = dashboardData.lastLoaded;
    const now = Date.now();
    
    if (!lastUpdate || (now - lastUpdate) > 30000) { // 30 seconds
      await updateAnalytics();
    }
    
    if (!lastUpdate || (now - lastUpdate) > 120000) { // 2 minutes  
      await updateJobs();
    }
  }, [dashboardData.lastLoaded, updateAnalytics, updateJobs]);

  // Setup background sync interval
  useEffect(() => {
    const interval = setInterval(backgroundSync, 30000); // Check every 30 seconds
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        backgroundSync(); // Sync immediately when tab becomes visible
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [backgroundSync]);

  // Load expert slots (preserved your exact implementation)
  const loadSlots = async () => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/expert-slots`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (mountedRef.current) {
          setDashboardData(prev => ({ ...prev, slots: data.slots }));
          console.log(`âœ… Loaded ${data.slots.length} expert slots`);
        }
      }
    } catch (error) {
      console.error('Failed to load slots:', error);
      if (mountedRef.current) {
        showSnackbar(`Failed to load slots: ${error.message}`, 'error');
      }
    }
  };

  // Load interviews (preserved your exact implementation)
  const loadInterviews = async () => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/interviews`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (mountedRef.current) {
          setDashboardData(prev => ({ ...prev, interviews: data.interviews || [] }));
          console.log(`âœ… Loaded ${data.interviews?.length || 0} interviews`);
        }
      }
    } catch (error) {
      console.error('Failed to load interviews:', error);
      if (mountedRef.current) {
        setDashboardData(prev => ({ ...prev, interviews: [] }));
      }
    }
  };

  // PRESERVED: All your existing slot creation functions exactly as they were
  const handleCreateSlot = async () => {
    try {
      console.log('ðŸ”§ Creating single slot with data:', newSlot);

      if (!newSlot.panelist_id) {
        showSnackbar('Please select a panelist', 'error');
        return;
      }

      if (!newSlot.date) {
        showSnackbar('Please select a date', 'error');
        return;
      }

      if (!newSlot.start_time) {
        showSnackbar('Please select start time', 'error');
        return;
      }

      if (!newSlot.end_time) {
        showSnackbar('Please select end time', 'error');
        return;
      }

      const available_from = combineDateTime(newSlot.date, newSlot.start_time);
      const available_to = combineDateTime(newSlot.date, newSlot.end_time);

      if (!available_from || !available_to) {
        showSnackbar('Invalid date/time combination', 'error');
        return;
      }

      if (new Date(available_to) <= new Date(available_from)) {
        showSnackbar('End time must be after start time', 'error');
        return;
      }

      const requestPayload = {
        panelist_id: parseInt(newSlot.panelist_id),
        available_from: available_from,
        available_to: available_to,
        is_recurring: false,
        notes: newSlot.notes || '',
        slot_type: newSlot.slot_type || 'interview'
      };

      console.log('ðŸ“¤ Sending request payload:', requestPayload);

      const response = await fetch(`${API_PREFIX}/admin/expert-slots`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('ðŸ“¥ Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('ðŸ“¥ Response data:', result);

        setSlotDialog(false);
        setNewSlot({
          panelist_id: '',
          date: '',
          start_time: '',
          end_time: '',
          notes: '',
          slot_type: 'interview'
        });

        await loadSlots();
        await loadAllSlotsForCalendar(); // ADDED: Refresh team calendar

        const slotsCreated = result.slots_created || 1;
        showSnackbar(`${slotsCreated} slot(s) created successfully!`, 'success');
      } else {
        const errorData = await response.json();
        console.error('â Œ Slot creation error:', errorData);
        showSnackbar(errorData.error || 'Failed to create slot', 'error');
      }
    } catch (error) {
      console.error('â Œ Network error:', error);
      showSnackbar(`Failed to create slot: ${error.message}`, 'error');
    }
  };

  // PRESERVED: Your exact advanced slot creation function
  const handleCreateAdvancedSlot = async () => {
    try {
      console.log('ðŸ”§ Creating advanced slot with data:', advancedSlot);

      if (!advancedSlot.panelist_id) {
        showSnackbar('Please select a panelist', 'error');
        return;
      }

      if (!advancedSlot.start_time) {
        showSnackbar('Please select start time', 'error');
        return;
      }

      if (!advancedSlot.end_time) {
        showSnackbar('Please select end time', 'error');
        return;
      }

      let available_from, available_to;

      if (advancedSlot.is_recurring) {
        const today = new Date().toISOString().split('T')[0];
        available_from = combineDateTime(today, advancedSlot.start_time);
        available_to = combineDateTime(today, advancedSlot.end_time);
      } else {
        if (!advancedSlot.date) {
          showSnackbar('Please select a date', 'error');
          return;
        }
        available_from = combineDateTime(advancedSlot.date, advancedSlot.start_time);
        available_to = combineDateTime(advancedSlot.date, advancedSlot.end_time);
      }

      if (!available_from || !available_to) {
        showSnackbar('Invalid date/time combination', 'error');
        return;
      }

      if (new Date(available_to) <= new Date(available_from)) {
        showSnackbar('End time must be after start time', 'error');
        return;
      }

      const requestPayload = {
        panelist_id: parseInt(advancedSlot.panelist_id),
        available_from: available_from,
        available_to: available_to,
        is_recurring: advancedSlot.is_recurring,
        recurrence_pattern: advancedSlot.recurrence_pattern,
        recurrence_days: Array.isArray(advancedSlot.recurrence_days)
          ? advancedSlot.recurrence_days.join(',')
          : advancedSlot.recurrence_days,
        recurrence_end_date: advancedSlot.recurrence_end_date ?
          new Date(advancedSlot.recurrence_end_date).toISOString() : null,
        notes: advancedSlot.notes || '',
        slot_type: advancedSlot.slot_type || 'interview'
      };

      console.log('ðŸ“¤ Sending advanced request payload:', requestPayload);

      const response = await fetch(`${API_PREFIX}/admin/expert-slots`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('ðŸ“¥ Advanced response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('ðŸ“¥ Advanced response data:', result);

        setAdvancedSlotDialog(false);
        setAdvancedSlot({
          panelist_id: '',
          date: '',
          start_time: '',
          end_time: '',
          is_recurring: false,
          recurrence_pattern: 'weekly',
          recurrence_days: ['mon', 'tue', 'wed', 'thu', 'fri'],
          recurrence_end_date: '',
          notes: '',
          slot_type: 'interview'
        });

        await loadSlots();
        await loadAllSlotsForCalendar(); // ADDED: Refresh team calendar

        const slotsCreated = result.slots_created || 0;
        showSnackbar(`${slotsCreated} slot(s) created successfully!`, 'success');
      } else {
        const errorData = await response.json();
        console.error('â Œ Advanced slot creation error:', errorData);
        showSnackbar(errorData.error || 'Failed to create slots', 'error');
      }
    } catch (error) {
      console.error('â Œ Advanced network error:', error);
      showSnackbar(`Failed to create slots: ${error.message}`, 'error');
    }
  };

  // Time change handlers (preserved)
  const handleAdvancedSlotTimeChange = (timeType, timeValue) => {
    console.log(`ðŸ•  Setting ${timeType} to:`, timeValue);
    setAdvancedSlot(prev => ({
      ...prev,
      [timeType]: timeValue
    }));
  };

  // PRESERVED: All your existing job management functions exactly as they were
const handleCreateJob = async () => {
  try {
    // 🚀 OPTIMISTIC UPDATE - Add job to UI immediately with temporary ID
    const tempJob = {
      ...newJob,
      id: `temp-${Date.now()}`, // Temporary ID
      created_at: new Date().toISOString(),
      candidate_count: 0,
      vendor_count: 0,
      panelist_count: 0,
      is_active: true
    };

    setDashboardData(prev => ({
      ...prev,
      jobs: [tempJob, ...prev.jobs],
      analytics: prev.analytics ? {
        ...prev.analytics,
        total_jobs: (prev.analytics.total_jobs || 0) + 1,
        active_jobs: (prev.analytics.active_jobs || 0) + 1
      } : prev.analytics
    }));

    setJobDialog(false);
    showSnackbar('Job created successfully!', 'success');

    const response = await fetch(`${API_PREFIX}/admin/jobs`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newJob),
    });

    if (response.ok) {
      const result = await response.json();
      const actualJob = result.job || result.data;
      
      // Replace temp job with real job from server
      setDashboardData(prev => ({
        ...prev,
        jobs: prev.jobs.map(job => 
          job.id === tempJob.id ? { ...actualJob, candidate_count: 0, vendor_count: 0, panelist_count: 0 } : job
        )
      }));

      setNewJob({
        title: '',
        description: '',
        requirements: '',
        location: '',
        salary_range: '',
        hiring_urgency: 'standard',
        expected_start_date: '',
        priority: 'medium',
        status: 'open'
      });

      // Sync analytics only (much faster than full refresh)
      setTimeout(() => updateAnalytics(), 1000);
    } else {
      // 🔄 ROLLBACK - Remove temp job on failure
      setDashboardData(prev => ({
        ...prev,
        jobs: prev.jobs.filter(job => job.id !== tempJob.id),
        analytics: prev.analytics ? {
          ...prev.analytics,
          total_jobs: Math.max(0, (prev.analytics.total_jobs || 0) - 1),
          active_jobs: Math.max(0, (prev.analytics.active_jobs || 0) - 1)
        } : prev.analytics
      }));

      const errorData = await response.json();
      showSnackbar(errorData.error || 'Failed to create job - removed', 'error');
    }
  } catch (error) {
    // 🔄 ROLLBACK on network error
    setDashboardData(prev => ({
      ...prev,
      jobs: prev.jobs.filter(job => !job.id.toString().startsWith('temp-')),
      analytics: prev.analytics ? {
        ...prev.analytics,
        total_jobs: Math.max(0, (prev.analytics.total_jobs || 0) - 1),
        active_jobs: Math.max(0, (prev.analytics.active_jobs || 0) - 1)
      } : prev.analytics
    }));
    showSnackbar(`Failed to create job: ${error.message} - removed`, 'error');
  }
};



const handleEditJob = (job) => {
  setEditJob({
    id: job.id,
    title: job.title,
    description: job.description,
    requirements: job.requirements,
    location: job.location,
    salary_range: job.salary_range,

    hiring_urgency: job.hiring_urgency || 'standard',
    expected_start_date: job.expected_start_date || '',
    // ✅ ADD CURRENCY FIELDS
    budget_min: job.budget_min || '',
    budget_max: job.budget_max || '',
    budget_currency: job.budget_currency || 'USD',
    priority: job.priority,
    status: job.status
  });
  setEditJobDialog(true);
};



  const handleUpdateJob = async () => {
    let previousJob = null;
    try {
      // 🚀 OPTIMISTIC UPDATE - Update job in UI immediately
      previousJob = dashboardData.jobs.find(job => job.id === editJob.id);
      setDashboardData(prev => ({
        ...prev,
        jobs: prev.jobs.map(job => 
          job.id === editJob.id ? { ...job, ...editJob } : job
        )
      }));

      setEditJobDialog(false);
      showSnackbar('Job updated successfully!', 'success');

      const response = await fetch(`${API_PREFIX}/admin/jobs/${editJob.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editJob),
      });

      if (!response.ok) {
        // 🔄 ROLLBACK - Restore previous job data on failure
        setDashboardData(prev => ({
          ...prev,
          jobs: prev.jobs.map(job => 
            job.id === editJob.id ? previousJob : job
          )
        }));

        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to update job - reverted', 'error');
      }
    } catch (error) {
      // 🔄 ROLLBACK on network error  
      setDashboardData(prev => ({
        ...prev,
        jobs: prev.jobs.map(job => 
          job.id === editJob.id ? previousJob : job
        )
      }));
      showSnackbar(`Failed to update job: ${error.message} - reverted`, 'error');
    }
  };

  const handleDeleteJob = async (jobId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Job',
      message: 'Are you sure you want to delete this job? This will also remove all associated assignments and data.',
      action: async () => {
        try {
          // 🚀 OPTIMISTIC UPDATE - Remove job from UI immediately
          const jobToDelete = dashboardData.jobs.find(job => job.id === jobId);
          setDashboardData(prev => ({
            ...prev,
            jobs: prev.jobs.filter(job => job.id !== jobId),
            analytics: prev.analytics ? {
              ...prev.analytics,
              total_jobs: Math.max(0, (prev.analytics.total_jobs || 0) - 1),
              active_jobs: jobToDelete?.is_active ? Math.max(0, (prev.analytics.active_jobs || 0) - 1) : prev.analytics.active_jobs
            } : prev.analytics
          }));
          
          showSnackbar('Job deleted successfully!', 'success');

          // Make API call in background
          const response = await fetch(`${API_PREFIX}/admin/jobs/${jobId}`, {
            method: 'DELETE',
            credentials: 'include',
          });

          if (!response.ok) {
            // 🔄 ROLLBACK - If API fails, restore the job
            setDashboardData(prev => ({
              ...prev,
              jobs: [...prev.jobs, jobToDelete].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
              analytics: prev.analytics ? {
                ...prev.analytics,
                total_jobs: (prev.analytics.total_jobs || 0) + 1,
                active_jobs: jobToDelete?.is_active ? (prev.analytics.active_jobs || 0) + 1 : prev.analytics.active_jobs
              } : prev.analytics
            }));
            
            const errorData = await response.json();
            showSnackbar(errorData.error || 'Failed to delete job - restored', 'error');
            return;
          }
          
          // ✅ Success - Optionally sync with server for accuracy
          setTimeout(() => handleManualRefresh(), 1000);
          
        } catch (error) {
          // 🔄 ROLLBACK on network error
          const jobToDelete = dashboardData.jobs.find(job => job.id === jobId);
          if (jobToDelete) {
            setDashboardData(prev => ({
              ...prev,
              jobs: [...prev.jobs, jobToDelete].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
              analytics: prev.analytics ? {
                ...prev.analytics,
                total_jobs: (prev.analytics.total_jobs || 0) + 1,
                active_jobs: jobToDelete?.is_active ? (prev.analytics.active_jobs || 0) + 1 : prev.analytics.active_jobs
              } : prev.analytics
            }));
          }
          showSnackbar(`Failed to delete job: ${error.message} - restored`, 'error');
        }
      }
    });
  };

  const handleQuickStatusUpdate = async (jobId, newStatus) => {
    let previousStatus = null;
    try {
      // 🚀 OPTIMISTIC UPDATE - Update status immediately
      previousStatus = dashboardData.jobs.find(job => job.id === jobId)?.status;
      setDashboardData(prev => ({
        ...prev,
        jobs: prev.jobs.map(job => 
          job.id === jobId ? { ...job, status: newStatus } : job
        )
      }));
      
      showSnackbar(`Job status updated to ${newStatus}!`, 'success');

      const response = await fetch(`${API_PREFIX}/admin/jobs/${jobId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        // 🔄 ROLLBACK - Restore previous status on failure
        setDashboardData(prev => ({
          ...prev,
          jobs: prev.jobs.map(job => 
            job.id === jobId ? { ...job, status: previousStatus } : job
          )
        }));
        
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to update status - reverted', 'error');
      }
    } catch (error) {
      // 🔄 ROLLBACK on network error
      setDashboardData(prev => ({
        ...prev,
        jobs: prev.jobs.map(job => 
          job.id === jobId ? { ...job, status: previousStatus } : job
        )
      }));
      showSnackbar(`Failed to update status: ${error.message} - reverted`, 'error');
    }
  };

  // PRESERVED: All your existing user management functions
  const handleCreateUser = async () => {
    try {
      // 🚀 OPTIMISTIC UPDATE - Add user to UI immediately
      const tempUser = {
        ...newUser,
        id: `temp-${Date.now()}`, // Temporary ID
        is_active: true,
        created_at: new Date().toISOString()
      };

      const targetArray = newUser.role === 'vendor' ? 'vendors' : 'panelists';
      setDashboardData(prev => ({
        ...prev,
        [targetArray]: [tempUser, ...prev[targetArray]]
      }));

      setUserDialog(false);
      showSnackbar(`${newUser.role} created successfully!`, 'success');

      const response = await fetch(`${API_PREFIX}/admin/users/create`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser),
      });

      if (response.ok) {
        const result = await response.json();
        const actualUser = result.user || result.data;

        // Replace temp user with real user from server
        setDashboardData(prev => ({
          ...prev,
          [targetArray]: prev[targetArray].map(user => 
            user.id === tempUser.id ? actualUser : user
          )
        }));

        setNewUser({
          username: '',
          email: '',
          password: '',
          role: 'vendor',
          company_name: '',
          contact_person: '',
          phone: '',
          expertise: '',
          experience_years: 5,
          department: 'HR'
        });

        // Sync analytics only
        setTimeout(() => updateAnalytics(), 1000);
      } else {
        // 🔄 ROLLBACK - Remove temp user on failure
        setDashboardData(prev => ({
          ...prev,
          [targetArray]: prev[targetArray].filter(user => user.id !== tempUser.id)
        }));

        const errorData = await response.json();
        showSnackbar(errorData.error || `Failed to create ${newUser.role} - removed`, 'error');
      }
    } catch (error) {
      // 🔄 ROLLBACK on network error
      const targetArray = newUser.role === 'vendor' ? 'vendors' : 'panelists';
      setDashboardData(prev => ({
        ...prev,
        [targetArray]: prev[targetArray].filter(user => !user.id.toString().startsWith('temp-'))
      }));
      showSnackbar(`Failed to create ${newUser.role}: ${error.message} - removed`, 'error');
    }
  };

  const handleEditUser = (user) => {
    setEditUser({
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role || 'vendor',
      company_name: user.company_name || '',
      contact_person: user.contact_person || '',
      phone: user.phone || '',
      expertise: user.expertise || '',
      experience_years: user.experience_years || 5,
      department: user.department || 'HR'
    });
    setEditUserDialog(true);
    handleMenuClose();
  };

  const handleUpdateUser = async () => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/users/${editUser.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editUser),
      });

      if (response.ok) {
        // 🚀 FAST UPDATE - Update user in state immediately instead of full refresh
        const targetArray = editUser.role === 'vendor' ? 'vendors' : 'panelists';
        setDashboardData(prev => ({
          ...prev,
          [targetArray]: prev[targetArray].map(user => 
            user.id === editUser.id ? { ...user, ...editUser } : user
          )
        }));

        setEditUserDialog(false);
        showSnackbar(`${editUser.role} updated successfully!`, 'success');
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to update user', 'error');
      }
    } catch (error) {
      showSnackbar(`Failed to update user: ${error.message}`, 'error');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'block' : 'unblock';
    const newStatus = !currentStatus;

    try {
      // 🚀 OPTIMISTIC UPDATE - Update user status immediately
      setDashboardData(prev => ({
        ...prev,
        vendors: prev.vendors.map(user => 
          user.id === userId ? { ...user, is_active: newStatus } : user
        ),
        panelists: prev.panelists.map(user => 
          user.id === userId ? { ...user, is_active: newStatus } : user
        ),
        // Managers too, or blocking one from the row menu calls the API but
        // leaves the row where it was until the next full refresh.
        managers: prev.managers.map(user =>
          user.id === userId ? { ...user, is_active: newStatus } : user
        )
      }));

      showSnackbar(`User ${action}ed successfully!`, 'success');
      handleMenuClose();

      const response = await fetch(`${API_PREFIX}/admin/users/${userId}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        // 🔄 ROLLBACK - Restore previous status on failure
        setDashboardData(prev => ({
          ...prev,
          vendors: prev.vendors.map(user => 
            user.id === userId ? { ...user, is_active: currentStatus } : user
          ),
          panelists: prev.panelists.map(user => 
            user.id === userId ? { ...user, is_active: currentStatus } : user
          ),
          managers: prev.managers.map(user =>
            user.id === userId ? { ...user, is_active: currentStatus } : user
          )
        }));

        const errorData = await response.json();
        showSnackbar(errorData.error || `Failed to ${action} user - reverted`, 'error');
      }
    } catch (error) {
      // 🔄 ROLLBACK on network error
      setDashboardData(prev => ({
        ...prev,
        vendors: prev.vendors.map(user => 
          user.id === userId ? { ...user, is_active: currentStatus } : user
        ),
        panelists: prev.panelists.map(user => 
          user.id === userId ? { ...user, is_active: currentStatus } : user
        )
      }));
      showSnackbar(`Failed to ${action} user: ${error.message} - reverted`, 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete User',
      message: `Are you sure you want to delete this user? This action cannot be undone.`,
      action: async () => {
        try {
          const response = await fetch(`${API_PREFIX}/admin/users/${userId}`, {
            method: 'DELETE',
            credentials: 'include',
          });

          if (response.ok) {
            // Optimistically remove the user from local lists so it disappears immediately
            setDashboardData(prev => ({
              ...prev,
              vendors: (prev.vendors || []).filter(u => u.id !== userId),
              panelists: (prev.panelists || []).filter(u => u.id !== userId),
              managers: (prev.managers || []).filter(u => u.id !== userId)
            }));
            showSnackbar('User deleted successfully!', 'success');
            // Keep the manual refresh to ensure backend + other counters stay in sync
            handleManualRefresh();
            handleMenuClose();
          } else {
            const errorData = await response.json();
            showSnackbar(errorData.error || 'Failed to delete user', 'error');
          }
        } catch (error) {
          showSnackbar(`Failed to delete user: ${error.message}`, 'error');
        }
      }
    });
  };

  // PRESERVED: All your existing assignment functions
  const handleOpenAssignmentDialog = async (job) => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/jobs/${job.id}/assignments`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAssignmentData({
          jobId: job.id,
          selectedVendors: [],
          selectedPanelists: [],
          currentVendors: data.vendors || [],
          currentPanelists: data.panelists || []
        });
      } else {
        setAssignmentData({
          jobId: job.id,
          selectedVendors: [],
          selectedPanelists: [],
          currentVendors: [],
          currentPanelists: []
        });
      }

      setSelectedJob(job);
      setAssignmentDialog(true);
    } catch (error) {
      console.error('Error loading assignments:', error);
      setAssignmentData({
        jobId: job.id,
        selectedVendors: [],
        selectedPanelists: [],
        currentVendors: [],
        currentPanelists: []
      });
      setSelectedJob(job);
      setAssignmentDialog(true);
    }
  };

  const handleAssignVendor = async (vendorId) => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/jobs/${assignmentData.jobId}/assign-vendor`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vendor_id: vendorId }),
      });

      if (response.ok) {
        showSnackbar('Vendor assigned successfully!', 'success');
        // 🚀 FAST UPDATE - Just refresh assignment dialog and update job counts
        handleOpenAssignmentDialog(selectedJob);
        // Only update the specific job's vendor count instead of full refresh
        setDashboardData(prev => ({
          ...prev,
          jobs: prev.jobs.map(job => 
            job.id === assignmentData.jobId 
              ? { ...job, vendor_count: (job.vendor_count || 0) + 1 }
              : job
          )
        }));
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to assign vendor', 'error');
      }
    } catch (error) {
      showSnackbar(`Failed to assign vendor: ${error.message}`, 'error');
    }
  };

  const handleAssignPanelist = async (panelistId) => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/jobs/${assignmentData.jobId}/assign-panelist`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ panelist_id: panelistId }),
      });

      if (response.ok) {
        showSnackbar('Panelist assigned successfully!', 'success');
        // 🚀 FAST UPDATE - Just refresh assignment dialog and update job counts
        handleOpenAssignmentDialog(selectedJob);
        // Only update the specific job's panelist count instead of full refresh
        setDashboardData(prev => ({
          ...prev,
          jobs: prev.jobs.map(job => 
            job.id === assignmentData.jobId 
              ? { ...job, panelist_count: (job.panelist_count || 0) + 1 }
              : job
          )
        }));
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to assign panelist', 'error');
      }
    } catch (error) {
      showSnackbar(`Failed to assign panelist: ${error.message}`, 'error');
    }
  };

  const handleRemoveAssignment = async (jobId, userId, type) => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/jobs/${jobId}/remove-assignment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, assignment_type: type }),
      });

      if (response.ok) {
        showSnackbar(`${type} removed successfully!`, 'success');
        // 🚀 FAST UPDATE - Just refresh assignment dialog and update job counts
        handleOpenAssignmentDialog(selectedJob);
        // Only update the specific job's assignment count instead of full refresh
        setDashboardData(prev => ({
          ...prev,
          jobs: prev.jobs.map(job => 
            job.id === jobId 
              ? { 
                  ...job, 
                  [`${type}_count`]: Math.max(0, (job[`${type}_count`] || 1) - 1)
                }
              : job
          )
        }));
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || `Failed to remove ${type}`, 'error');
      }
    } catch (error) {
      showSnackbar(`Failed to remove ${type}: ${error.message}`, 'error');
    }
  };

  // PRESERVED: Your slot and interview management functions
  const handleDeleteSlot = async (slotId) => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/expert-slots/${slotId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // 🚀 FAST UPDATE - Remove slot from state immediately
        setDashboardData(prev => ({
          ...prev,
          slots: prev.slots.filter(slot => slot.id !== slotId)
        }));
        setAllSlots(prev => prev.filter(slot => slot.id !== slotId));
        
        showSnackbar('Expert slot deleted successfully!', 'success');
        // Only reload if absolutely necessary for sync
        setTimeout(() => {
          loadSlots();
          loadAllSlotsForCalendar();
        }, 1000);
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error, 'error');
      }
    } catch (error) {
      showSnackbar(`Failed to delete slot: ${error.message}`, 'error');
    }
  };

  const handleCancelInterview = async (interviewId, reason = 'Cancelled by admin') => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/interviews/${interviewId}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        loadInterviews();
        showSnackbar('Interview cancelled successfully!', 'success');
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to cancel interview', 'error');
      }
    } catch (error) {
      showSnackbar(`Failed to cancel interview: ${error.message}`, 'error');
    }
  };

  /**
   * Admin stat card.
   *
   * Now a thin adapter over the shared StatCard, keeping this call signature
   * (title / value / icon / color / subtitle / onClick) so the existing call
   * sites are untouched while the rendering matches the rest of the product.
   *
   * What it replaces: a gradient-tinted card with an h3 figure above an h6
   * label, a 64px filled avatar, a 4px gradient top rule and a lift-and-scale
   * hover. The manager, vendor and panelist dashboards all render the shared
   * version -- label above, figure below, quiet 32px icon, hairline border --
   * so the admin overview was the one screen that looked like another product.
   *
   * `color` was a free-form hex per call site. It is mapped onto the semantic
   * tones instead, which is what keeps the same meaning the same colour across
   * screens.
   */
  const STAT_TONE_BY_COLOR = {
    '#5c6ac4': 'info',     // jobs
    '#3B4EA0': 'info',
    '#ff9800': 'warning',  // users
    '#4caf50': 'success',  // hires / active
    '#388e3c': 'success',
    '#00acc1': 'info',     // reviews
    '#e91e63': 'danger',   // interviews
    '#f44336': 'danger',
  };

  const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
    <UiStatCard
      label={title}
      value={value}
      caption={subtitle}
      icon={icon}
      tone={STAT_TONE_BY_COLOR[color] || 'neutral'}
      onClick={onClick}
    />
  );



 // Enhanced date formatting with timestamp
 const JobsManagementSection = () => {
  // 🚀 PERFORMANCE: Memoized filtering for better performance
  const filteredJobs = useMemo(() => {
    if (!dashboardData.jobs) return [];
    
    return dashboardData.jobs.filter(job => {
      const matchesSearch = job.title?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                           job.location?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                           job.job_code?.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                           job.job_reference?.toLowerCase().includes(jobSearchTerm.toLowerCase());

      const matchesStatus = jobsStatusFilter === 'all' || job.status === jobsStatusFilter;
      const matchesPriority = priorityFilter === 'all' || job.priority === priorityFilter;
      const matchesSubPriority = subPriorityFilter === 'all' || job.sub_priority === subPriorityFilter;

      return matchesSearch && matchesStatus && matchesPriority && matchesSubPriority;
    });
  }, [dashboardData.jobs, jobSearchTerm, jobsStatusFilter, priorityFilter, subPriorityFilter]);

  // 🚀 PERFORMANCE: Memoized pagination slice
  const paginatedJobs = useMemo(() => {
    const startIndex = (jobsPage - 1) * jobsPerPage;
    return filteredJobs.slice(startIndex, startIndex + jobsPerPage);
  }, [filteredJobs, jobsPage, jobsPerPage]);

  // ✅ NEW: Enhanced budget formatting with currency support
  const formatBudget = (job) => {
    // First try to use formatted_budget from backend
    if (job.formatted_budget && job.formatted_budget !== 'Not specified') {
      return job.formatted_budget;
    }

    // If we have structured budget fields, format them
    if (job.budget_min || job.budget_max) {
      const currencySymbols = {
        'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'INR': '₹',
        'CAD': 'C$', 'AUD': 'A$', 'CHF': 'CHF', 'CNY': '¥', 'SGD': 'S$'
      };

      const symbol = currencySymbols[job.budget_currency] || job.budget_currency || '$';

      if (job.budget_min && job.budget_max) {
        const minFormatted = `${symbol}${parseInt(job.budget_min).toLocaleString()}`;
        const maxFormatted = `${symbol}${parseInt(job.budget_max).toLocaleString()}`;
        return `${minFormatted} - ${maxFormatted}`;
      } else if (job.budget_max) {
        return `Up to ${symbol}${parseInt(job.budget_max).toLocaleString()}`;
      } else if (job.budget_min) {
        return `From ${symbol}${parseInt(job.budget_min).toLocaleString()}`;
      }
    }

    // Fallback to legacy salary_range
    return job.salary_range || 'Not specified';
  };

  // ✅ NEW: Budget display with currency indicator
  const getBudgetDisplay = (job) => {
    const budgetText = formatBudget(job);
    const currency = job.budget_currency || 'USD';

    return {
      text: budgetText,
      currency: currency,
      isStructured: !!(job.budget_min || job.budget_max),
      isLegacy: !!(job.salary_range && !job.budget_min && !job.budget_max)
    };
  };

  // Enhanced date formatting with timestamp
  const formatCreatedDate = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short'
        }),
        time: date.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
    } catch {
      return { date: 'N/A', time: '' };
    }
  };

  // Compact job code display
  const getJobDisplayCode = (job) => {
    if (job.job_code && job.job_code.trim()) {
      return job.job_code;
    }
    return `JOB-${job.id}`;
  };

  // Simplified status colors
  const getStatusColor = (status) => {
    switch(status) {
      case 'open': return '#4caf50';
      case 'closed': return '#f44336';
      case 'filled': return '#2196f3';
      case 'on_hold': return '#ff9800';
      default: return '#757575';
    }
  };

  return (

    <Card>
      <CardContent sx={{ p: 2 }}>
        {/* Compact Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
            Job Management ({filteredJobs.length})
          </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
  <TextField
    size="small"
    placeholder="Search by Job title, Location, or Job ID..."
    value={jobSearchTerm}
    onChange={(e) => setJobSearchTerm(e.target.value)}
    sx={{
      width: 280, // wider so text fits
      '& .MuiOutlinedInput-root': {
        height: 40, // input height
      },
      '& input::placeholder': {
        fontSize: '1rem', // ✅ increase placeholder font size
        color: '#666',    // optional darker shade
      },
    }}
  />




            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => setJobDialog(true)}
              sx={{
                height: 36,
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Add Job
            </Button>
          </Box>
        </Box>
        {/* ✅ ADD THIS RIGHT BEFORE {/* ✅ COMPLETE COMBINED FILTER - Replace both filter sections with this ONE */}
<Card sx={{ mb: 3 }}>
  <CardContent>
    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Search /> Enhanced Job Filters
    </Typography>
    <Grid container spacing={2} alignItems="center">
      {/* Search Field */}


      {/* Status Filter */}
      <Grid item xs={12} md={1.5}>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={jobsStatusFilter}
            label="Status"
            onChange={(e) => setJobsStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="open" sx={{ color: '#2e7d32' }}>🟢 Open</MenuItem>
            <MenuItem value="filled" sx={{ color: '#1565c0' }}>✅ Filled</MenuItem>
            <MenuItem value="closed" sx={{ color: '#d32f2f' }}>🔴 Closed</MenuItem>
            <MenuItem value="on_hold" sx={{ color: '#ef6c00' }}>🟡 On Hold</MenuItem>
            <MenuItem value="draft" sx={{ color: '#5e35b1' }}>📝 Draft</MenuItem>
            <MenuItem value="deadline_passed" sx={{ color: 'error.main' }}>
              ⚠️ Deadline Passed
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Priority Filter */}
      <Grid item xs={12} md={1.5}>
        <FormControl fullWidth size="small">
          <InputLabel>Priority</InputLabel>
          <Select
            value={priorityFilter}
            label="Priority"
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <MenuItem value="all">All Priority</MenuItem>
            <MenuItem value="urgent" sx={{ color: '#d32f2f' }}>🔴 Urgent</MenuItem>
            <MenuItem value="high" sx={{ color: '#f44336' }}>🟠 High</MenuItem>
            <MenuItem value="medium" sx={{ color: '#ff9800' }}>🟡 Medium</MenuItem>
            <MenuItem value="low" sx={{ color: '#4caf50' }}>🟢 Low</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Sub-Priority Filter */}
      <Grid item xs={12} md={1.5}>
        <FormControl fullWidth size="small">
          <InputLabel>Sub-Priority</InputLabel>
          <Select
            value={subPriorityFilter}
            label="Sub-Priority"
            onChange={(e) => setSubPriorityFilter(e.target.value)}
          >
            <MenuItem value="all">All Sub-Priority</MenuItem>
            <MenuItem value="h1">H1 (Highest)</MenuItem>
            <MenuItem value="h2">H2</MenuItem>
            <MenuItem value="h3">H3</MenuItem>
            <MenuItem value="m1">M1 (Highest)</MenuItem>
            <MenuItem value="m2">M2</MenuItem>
            <MenuItem value="m3">M3</MenuItem>
            <MenuItem value="l1">L1 (Highest)</MenuItem>
            <MenuItem value="l2">L2</MenuItem>
            <MenuItem value="l3">L3</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Sort By Filter */}
      <Grid item xs={12} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => setSortBy(e.target.value)}
          >
            <MenuItem value="priority_desc">🔥 Priority (High→Low)</MenuItem>
            <MenuItem value="priority_asc">📈 Priority (Low→High)</MenuItem>
            <MenuItem value="date_desc">🕐 Newest First</MenuItem>
            <MenuItem value="date_asc">⏰ Oldest First</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Clear Filters Button */}
      <Grid item xs={12} md={1.5}>
        <Button
          variant="outlined"
          onClick={() => {
            setJobSearchTerm('');
            setJobsStatusFilter('all');
            setPriorityFilter('all');
            setSubPriorityFilter('all');
            setSortBy('priority_desc');
          }}
          startIcon={<Clear />}
          fullWidth
          size="small"
        >
          Clear Filters
        </Button>
      </Grid>
    </Grid>
  </CardContent>
</Card>

        {/* ✅ ENHANCED: Updated Table with Budget Column */}
        <TableContainer
          component={Paper}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            '& .MuiTableCell-root': {
              borderBottom: '1px solid #f0f0f0',
              py: 1,
              px: 1.5,
              verticalAlign: 'middle'
            }
          }}
        >
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  width: '25%',
                  textAlign: 'left'
                }}>
                  Job Code & Title
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  width: '12%',
                  textAlign: 'left'
                }}>
                  Location
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  width: '15%',
                  textAlign: 'left'
                }}>
                  Budget/Salary
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  width: '8%',
                  textAlign: 'center'
                }}>
                  Status
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  width: '8%',
                  textAlign: 'center'
                }}>
                  Priority
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  width: '8%',
                  textAlign: 'center'
                }}>
                  Assigned
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  width: '10%',
                  textAlign: 'center'
                }}>
                  Created
                </TableCell>
                <TableCell sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  width: '14%',
                  textAlign: 'center'
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedJobs.map((job, index) => {
                  const budgetInfo = getBudgetDisplay(job);

                  return (
                    <TableRow
                      key={job.id}
                      sx={{
                        '&:hover': { bgcolor: '#f8fafe' },
                        '&:nth-of-type(even)': { bgcolor: '#fafbfc' },
                        height: 50
                      }}
                    >
                      {/* Job Title & Code */}
                      <TableCell sx={{
                        textAlign: 'left',
                        verticalAlign: 'middle',
                        py: 1,
                        px: 1.5
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}>
                          {/* Primary: Job Title (Most Prominent) */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                color: '#1a1a1a',
                                lineHeight: 1.3,
                                mb: 0.25
                              }}
                            >
                              {job.title}
                            </Typography>

                            {/* Secondary: Job Reference if available */}
                            {job.job_reference && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.75rem',
                                  display: 'block'
                                }}
                              >
                                Ref: {job.job_reference}
                              </Typography>
                            )}
                          </Box>

                          {/* Tertiary: Job Code (Small, Unobtrusive) */}
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.7rem',
                              color: '#666',
                              bgcolor: '#f0f0f0',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontWeight: 500,
                              flexShrink: 0,
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            {getJobDisplayCode(job)}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Location - Left Aligned */}
                      <TableCell sx={{ textAlign: 'left', verticalAlign: 'middle' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {job.location}
                        </Typography>
                      </TableCell>

                      {/* ✅ NEW: Enhanced Budget/Salary Column */}
                      <TableCell sx={{ textAlign: 'left', verticalAlign: 'middle' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: budgetInfo.isStructured ? 600 : 400,
                              color: budgetInfo.isStructured ? '#1a1a1a' : 'text.secondary'
                            }}
                          >
                            {budgetInfo.text}
                          </Typography>

                          {/* Currency indicator for structured budgets */}
                          {budgetInfo.isStructured && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={budgetInfo.currency}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: '#e3f2fd',
                                  color: '#1976d2',
                                  fontWeight: 500,
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#4caf50' }}>
                                Structured
                              </Typography>
                            </Box>
                          )}

                          {/* Legacy indicator */}
                          {budgetInfo.isLegacy && (
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ff9800' }}>
                              Legacy Range
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Status - Center Aligned */}
                      <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Chip
                          label={job.status.toUpperCase()}
                          size="small"
                          sx={{
                            height: 26,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            bgcolor: getStatusColor(job.status),
                            color: 'white',
                            minWidth: 70,
                            boxShadow: `0 2px 4px ${getStatusColor(job.status)}30`,
                            '& .MuiChip-label': { 
                              px: 1.5,
                              py: 0.5
                            },
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 4px 8px ${getStatusColor(job.status)}40`,
                              bgcolor: getStatusColor(job.status),
                              filter: 'brightness(1.1)'
                            },
                            transition: 'all 0.2s ease-in-out'
                          }}
                        />
                      </TableCell>

                      {/* Priority - Center Aligned */}
{/* ✅ REPLACE the existing Priority TableCell with: */}
<TableCell sx={{ textAlign: 'center', verticalAlign: 'middle', minWidth: 120 }}>
  <PriorityDisplay
    priority={job.priority}
    subPriority={job.sub_priority}
    deadline={job.deadline}
    isDeadlinePassed={job.is_deadline_passed}
  />
</TableCell>


                      {/* ✅ REPLACED: Assignments column with hover-to-view Tooltip */}
                      <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Tooltip
                          title={
                            hoveredJob === job.id && assignmentTooltips[job.id] ? (
                              <Box sx={{ p: 1, maxWidth: 300 }}>
                                {assignmentTooltips[job.id].vendors.length > 0 && (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.light', mb: 0.5 }}>
                                      📊 Vendors ({assignmentTooltips[job.id].vendors.length})
                                    </Typography>
                                    {assignmentTooltips[job.id].vendors.map((vendor, idx) => (
                                      <Typography key={idx} variant="body2" sx={{ ml: 1, fontSize: '0.8rem' }}>
                                        • {vendor.username} - {vendor.company_name}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}

                                {assignmentTooltips[job.id].panelists.length > 0 && (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.light', mb: 0.5 }}>
                                      👥 Panelists ({assignmentTooltips[job.id].panelists.length})
                                    </Typography>
                                    {assignmentTooltips[job.id].panelists.map((panelist, idx) => (
                                      <Typography key={idx} variant="body2" sx={{ ml: 1, fontSize: '0.8rem' }}>
                                        • {panelist.username} - {panelist.expertise}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}

                                {assignmentTooltips[job.id].vendors.length === 0 && assignmentTooltips[job.id].panelists.length === 0 && (
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    No assignments yet
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              "Loading assignments..."
                            )
                          }
                          arrow
                          enterDelay={500}
                          leaveDelay={200}
                          placement="top"
                        >
                          <Box
                            sx={{
                              cursor: 'pointer',
                              display: 'inline-block',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                borderRadius: '4px'
                              },
                              p: 0.5,
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={async () => {
                              setHoveredJob(job.id);
                              await fetchAssignmentTooltip(job.id);
                            }}
                            onMouseLeave={() => {
                              setHoveredJob(null);
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                              V:{job.vendor_count || 0} P:{job.panelist_count || 0}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>

                      {/* Created Date with Timestamp - Center Aligned */}
                      <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                            {formatCreatedDate(job.created_at).date}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            {formatCreatedDate(job.created_at).time}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Actions - Center Aligned */}
                      <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Edit Job">
                            <IconButton
                              size="small"
                              onClick={() => handleEditJob(job)}
                              sx={{
                                width: 28,
                                height: 28,
                                color: '#1976d2',
                                '&:hover': { bgcolor: '#e3f2fd' }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Manage Assignments">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenAssignmentDialog(job)}
                              sx={{
                                width: 28,
                                height: 28,
                                color: '#ed6c02',
                                '&:hover': { bgcolor: '#fff3e0' }
                              }}
                            >
                              <Assignment fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Job">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteJob(job.id)}
                              sx={{
                                width: 28,
                                height: 28,
                                color: '#d32f2f',
                                '&:hover': { bgcolor: '#ffebee' }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

              {/* Empty State */}
              {filteredJobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Compact Pagination */}
        {filteredJobs.length > jobsPerPage && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            pt: 1.5,
            borderTop: '1px solid #e0e0e0'
          }}>
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
              {((jobsPage - 1) * jobsPerPage) + 1}-{Math.min(jobsPage * jobsPerPage, filteredJobs.length)} of {filteredJobs.length}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                disabled={jobsPage === 1}
                onClick={() => setJobsPage(prev => prev - 1)}
                sx={{ width: 28, height: 28 }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>

              <Typography variant="caption" sx={{
                px: 1,
                py: 0.5,
                bgcolor: '#f5f5f5',
                borderRadius: 0.5,
                fontSize: '0.75rem',
                minWidth: 40,
                textAlign: 'center'
              }}>
                {jobsPage}/{Math.ceil(filteredJobs.length / jobsPerPage)}
              </Typography>

              <IconButton
                size="small"
                disabled={jobsPage === Math.ceil(filteredJobs.length / jobsPerPage)}
                onClick={() => setJobsPage(prev => prev + 1)}
                sx={{ width: 28, height: 28 }}
              >
                <ArrowForward fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};




  /**
   * User Management.
   *
   * Rebuilt on the shared primitives in components/shared/ui.js — the same ones
   * the manager, vendor and panelist dashboards already use. The admin area was
   * the only part of the product still hand-rolling Card + h5 + a per-section
   * gradient button, which is why it read as a different application: this
   * screen alone invented a teal accent, Interview Management a pink one and
   * Slot Management a green one, none of them the product's indigo.
   *
   * Behaviour is unchanged. The tables list accounts that are not blocked and
   * the Blocked Users panel lists the ones that are, both via isBlocked() so a
   * missing is_active can never put an account in both places at once.
   */
  const UsersManagementSection = () => {
    const activeVendors = dashboardData.vendors.filter((v) => !isBlocked(v));
    const activePanelists = dashboardData.panelists.filter((p) => !isBlocked(p));
    const activeManagers = dashboardData.managers.filter((m) => !isBlocked(m));
    // Managers are included here too. They could always be created, but nothing
    // listed them, so a blocked manager was invisible and there was no way to
    // restore their access from the admin screens.
    const blockedUsers = [
      ...dashboardData.vendors,
      ...dashboardData.panelists,
      ...dashboardData.managers,
    ].filter(isBlocked);

    const overflowNote = (shown, total) =>
      total > shown ? `Showing ${shown} of ${total}` : `${total} total`;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SectionHeader
          title="User Management"
          subtitle="Vendors, panelists and role assignments"
          actions={
            <Button variant="contained" startIcon={<Add />} onClick={() => setUserDialog(true)}>
              Create User
            </Button>
          }
        />

        <Grid container spacing={3} alignItems="stretch">
          {/* Vendors ------------------------------------------------------ */}
          <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
            <Section
              title="Vendors"
              subtitle={overflowNote(Math.min(20, activeVendors.length), activeVendors.length)}
              disablePadding
              sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {activeVendors.length === 0 ? (
                <EmptyState
                  dense
                  icon={<Work />}
                  title="No vendors yet"
                  description="Create a vendor account to start receiving candidate submissions."
                  action={
                    <Button size="small" variant="outlined" startIcon={<Add />}
                            onClick={() => setUserDialog(true)}>
                      Create User
                    </Button>
                  }
                />
              ) : (
                <Box sx={{ overflowX: 'auto', maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Jobs</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeVendors.slice(0, 20).map((vendor) => (
                        <UserRow
                          key={vendor.id}
                          user={vendor}
                          accent={tokens.color.accent}
                          secondary={vendor.company_name}
                          countIcon={<Work sx={{ fontSize: 16 }} />}
                          count={vendor.assigned_jobs}
                        >
                          <Tooltip title="Actions">
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, vendor)}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </UserRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Section>
          </Grid>

          {/* Panelists ---------------------------------------------------- */}
          <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
            <Section
              title="Panelists"
              subtitle={overflowNote(Math.min(20, activePanelists.length), activePanelists.length)}
              disablePadding
              sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {activePanelists.length === 0 ? (
                <EmptyState
                  dense
                  icon={<RateReview />}
                  title="No panelists yet"
                  description="Panelists review CVs and run interviews."
                  action={
                    <Button size="small" variant="outlined" startIcon={<Add />}
                            onClick={() => setUserDialog(true)}>
                      Create User
                    </Button>
                  }
                />
              ) : (
                <Box sx={{ overflowX: 'auto', maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Expertise</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Reviews</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activePanelists.slice(0, 20).map((panelist) => (
                        <UserRow
                          key={panelist.id}
                          user={panelist}
                          accent={tokens.color.info}
                          secondary={panelist.expertise}
                          countIcon={<RateReview sx={{ fontSize: 16 }} />}
                          count={panelist.cv_reviews}
                        >
                          <Tooltip title="Manage slots">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedPanelist(panelist.id);
                                setTabValue(4);
                              }}
                            >
                              <CalendarIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Actions">
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, panelist)}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </UserRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Section>
          </Grid>

          {/* Hiring managers ---------------------------------------------- */}
          <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
            <Section
              title="Hiring managers"
              subtitle={activeManagers.length > 20
                ? `Showing 20 of ${activeManagers.length}`
                : `${activeManagers.length} total`}
              disablePadding
              sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {activeManagers.length === 0 ? (
                <EmptyState
                  dense
                  icon={<ManageAccounts />}
                  title="No hiring managers yet"
                  description="Managers raise requirements, which become jobs for vendors to source against."
                  action={
                    <Button size="small" variant="outlined" startIcon={<Add />}
                            onClick={() => setUserDialog(true)}>
                      Create User
                    </Button>
                  }
                />
              ) : (
                <Box sx={{ overflowX: 'auto', maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Requirements</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeManagers.slice(0, 20).map((manager) => (
                        <UserRow
                          key={manager.id}
                          user={manager}
                          accent={tokens.color.success}
                          secondary={manager.department || manager.company_name}
                          countIcon={<Assignment sx={{ fontSize: 16 }} />}
                          count={manager.requirements_raised}
                        >
                          <Tooltip title="Actions">
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, manager)}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </UserRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Section>
          </Grid>

          {/* Blocked users ------------------------------------------------ */}
          <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
            <Section
              title="Blocked users"
              subtitle="Accounts an administrator has revoked"
              disablePadding
              sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            >
              {blockedUsers.length === 0 ? (
                <EmptyState
                  dense
                  icon={<CheckCircle />}
                  title="No blocked users"
                  description="Every manager, vendor and panelist currently has access."
                />
              ) : (
                <List disablePadding>
                  {blockedUsers.map((user) => (
                    <ListItem
                      key={`${user.role}-${user.id}`}
                      divider
                      secondaryAction={
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleToggleUserStatus(user.id, false)}
                        >
                          Unblock
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={user.username}
                        secondary={[user.role, user.email].filter(Boolean).join(' · ')}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 550 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Section>
          </Grid>

          {/* System health ------------------------------------------------ */}
          <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
            <Section
              title="System health"
              subtitle="Current platform status"
              sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {[
                  { label: 'Active sessions', value: <StatusChip status="active" label="Users online" /> },
                  { label: 'Database', value: <StatusChip status="active" label="Healthy" /> },
                  { label: 'Last backup', value: (
                      <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }}>
                        Auto-backup enabled
                      </Typography>
                    ) },
                  { label: 'Slot management', value: <StatusChip status="scheduled" label="Active" /> },
                ].map((row, i, arr) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 1.25,
                      borderBottom: i < arr.length - 1 ? `1px solid ${tokens.color.border}` : 'none',
                    }}
                  >
                    <Typography variant="body2">{row.label}</Typography>
                    {row.value}
                  </Box>
                ))}
              </Box>
            </Section>
          </Grid>

          {/* Role assignments --------------------------------------------- */}
          <Grid item xs={12}>
            <Section
              title="User role management"
              subtitle="Accounts with a predetermined role and login method"
              disablePadding
              actions={
                <Button
                  variant="outlined"
                  startIcon={<ManageAccounts />}
                  onClick={() => setShowAssignDialog(true)}
                >
                  Assign user role
                </Button>
              }
            >
              {roleAssignments.length === 0 ? (
                <EmptyState
                  dense
                  icon={<ManageAccounts />}
                  title="No role assignments yet"
                  description="Assign a role to grant someone access and fix how they sign in."
                  action={
                    <Button size="small" variant="outlined" startIcon={<ManageAccounts />}
                            onClick={() => setShowAssignDialog(true)}>
                      Assign user role
                    </Button>
                  }
                />
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Full name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Department / company</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roleAssignments.map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell sx={{ fontWeight: 550 }}>
                            {user.full_name || user.username || '—'}
                          </TableCell>
                          <TableCell sx={{ color: tokens.color.inkSecondary }}>{user.email}</TableCell>
                          <TableCell><StatusChip status="info" label={user.role} /></TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{user.user_type}</TableCell>
                          <TableCell sx={{ color: tokens.color.inkSecondary }}>
                            {(user.user_type === 'internal' ? user.department : user.company_name) || '—'}
                          </TableCell>
                          <TableCell>
                            <StatusChip status={isBlocked(user) ? 'blocked' : 'active'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Section>
          </Grid>
        </Grid>
      </Box>
    );
  };



  const InterviewManagementSection = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SectionHeader
        title="Interview Management"
        subtitle={`${dashboardData.interviews.length} scheduled`}
        actions={
          <Button
            variant="contained"
            startIcon={<VideoCall />}
            onClick={() => showSnackbar('Interview booking feature coming soon!', 'info')}
          >
            Book Interview
          </Button>
        }
      />
      <Section disablePadding>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Candidate</TableCell>
                <TableCell>Job Title</TableCell>
                <TableCell>Panelist</TableCell>
                <TableCell>Scheduled Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dashboardData.interviews.map((interview) => (
                <TableRow key={interview.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {interview.candidate_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {interview.candidate_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{interview.job_title}</TableCell>
                  <TableCell>{interview.panelist_name}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(interview.scheduled_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(interview.scheduled_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={interview.status}
                      color={
                        interview.status === 'completed' ? 'success' :
                        interview.status === 'scheduled' ? 'primary' :
                        interview.status === 'cancelled' ? 'error' : 'warning'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Cancel Interview">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setConfirmDialog({
                              open: true,
                              title: 'Cancel Interview',
                              message: `Are you sure you want to cancel the interview with ${interview.candidate_name}?`,
                              action: () => handleCancelInterview(interview.id)
                            });
                          }}
                          disabled={interview.status === 'completed' || interview.status === 'cancelled'}
                        >
                          <EventBusyIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {dashboardData.interviews.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <VideoCall sx={{ fontSize: 48, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        No interviews scheduled yet. Interviews will appear here once candidates are booked for expert slots.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Box>
  );

  // ADDED: New Slot Management Section
  // ENHANCED: Slot Management Section with Creation Interface
const SlotManagementSection = () => {
  const [panelistSlots, setPanelistSlots] = useState([]);
  const [loadingPanelistSlots, setLoadingPanelistSlots] = useState(false);
  const [showCreateSlotDialog, setShowCreateSlotDialog] = useState(false);
  const [newSlotData, setNewSlotData] = useState({
    panelist_id: selectedPanelist,
    date: '',
    start_time: '',
    end_time: '',
    notes: '',
    slot_type: 'interview'
  });

  // Load slots for selected panelist
  const loadPanelistSlots = useCallback(async () => {
  if (!selectedPanelist) {
    console.log('ðŸ“  No panelist selected, clearing slots');
    setPanelistSlots([]);
    return;
  }

  try {
    setLoadingPanelistSlots(true);
    console.log(`ðŸ“… Loading slots for panelist ${selectedPanelist}`);

    const response = await fetch(`${API_PREFIX}/admin/panelists/${selectedPanelist}/availability-slots`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    console.log(`ðŸ“¡ Response status: ${response.status}`);

    if (!response.ok) {
      const errorData = await response.json();
      console.error('â Œ API Error:', errorData);
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('ðŸ“¥ Received data:', data);

    setPanelistSlots(data.slots || []);
    console.log(`âœ… Loaded ${data.slots?.length || 0} slots for ${data.panelist_name}`);

  } catch (error) {
    console.error('â Œ Failed to load panelist slots:', error);
    setPanelistSlots([]);
    showSnackbar(`Failed to load slots: ${error.message}`, 'error');
  } finally {
    setLoadingPanelistSlots(false);
  }
}, [selectedPanelist]);
const debugPanelistSlots = useCallback(async () => {
  if (!selectedPanelist) return;

  try {
    console.log(`ðŸ”  Debug: Testing API for panelist ${selectedPanelist}`);

    const response = await fetch(`${API_PREFIX}/admin/panelists/${selectedPanelist}/debug-slots`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    });

    const data = await response.json();
    console.log('ðŸ”  Debug Response:', data);

    showSnackbar(`Debug: Found ${data.expert_slots_count} slots in database`, 'info');

  } catch (error) {
    console.error('ðŸ”  Debug Error:', error);
    showSnackbar(`Debug failed: ${error.message}`, 'error');
  }
}, [selectedPanelist]);


  // Create new slot
  const handleCreatePanelistSlot = async () => {
    try {
      if (!selectedPanelist || !newSlotData.date || !newSlotData.start_time || !newSlotData.end_time) {
        showSnackbar('Please fill all required fields', 'error');
        return;
      }

      const startDateTime = combineDateTime(newSlotData.date, newSlotData.start_time);
      const endDateTime = combineDateTime(newSlotData.date, newSlotData.end_time);

      if (!startDateTime || !endDateTime) {
        showSnackbar('Invalid date/time combination', 'error');
        return;
      }

      const requestPayload = {
        start_datetime: startDateTime,
        end_datetime: endDateTime,
        notes: newSlotData.notes || '',
        slot_type: newSlotData.slot_type || 'interview',
        is_available: true
      };

      console.log('ðŸ“¤ Creating slot for panelist:', selectedPanelist, requestPayload);

      const response = await fetch(`${API_PREFIX}/admin/panelists/${selectedPanelist}/availability-slots`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('âœ… Slot created successfully:', result);

        setShowCreateSlotDialog(false);
        setNewSlotData({
          panelist_id: selectedPanelist,
          date: '',
          start_time: '',
          end_time: '',
          notes: '',
          slot_type: 'interview'
        });

        // Reload slots
        await loadPanelistSlots();
        await loadAllSlotsForCalendar();

        showSnackbar('Slot created successfully!', 'success');
      } else {
        const errorData = await response.json();
        console.error('â Œ Slot creation error:', errorData);
        showSnackbar(errorData.error || 'Failed to create slot', 'error');
      }
    } catch (error) {
      console.error('â Œ Network error:', error);
      showSnackbar(`Failed to create slot: ${error.message}`, 'error');
    }
  };

  // Delete slot
  const handleDeleteSlot = async (slotId) => {
    try {
      const response = await fetch(`${API_PREFIX}/admin/expert-slots/${slotId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await loadPanelistSlots();
        await loadAllSlotsForCalendar();
        showSnackbar('Slot deleted successfully!', 'success');
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to delete slot', 'error');
      }
    } catch (error) {
      showSnackbar(`Failed to delete slot: ${error.message}`, 'error');
    }
  };


  // Load slots when panelist changes
  useEffect(() => {
  console.log(`ðŸ”„ Panelist changed to: ${selectedPanelist}`);
  if (selectedPanelist) {
    loadPanelistSlots();
  } else {
    setPanelistSlots([]);
  }
}, [selectedPanelist, loadPanelistSlots]);


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SectionHeader
        title="Slot Management"
        subtitle="Create and review panelist availability"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowCreateSlotDialog(true)}
              disabled={!selectedPanelist}
            >
              Create Slot
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={() => setTeamCalendarDialog(true)}
            >
              Team Calendar
            </Button>
          </Box>
        }
      />

        {/* Panelist Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Panelist</InputLabel>
          <Select
            value={selectedPanelist}
            onChange={(e) => setSelectedPanelist(e.target.value)}
            label="Select Panelist"
          >
            <MenuItem value="">
              <em>Choose a panelist...</em>
            </MenuItem>
            {dashboardData.panelists.map((panelist) => (
              <MenuItem key={panelist.id} value={panelist.id}>
                <Box>
                  <Typography variant="subtitle2">{panelist.username}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Panelist Slots Display */}
        {selectedPanelist && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Slots for {dashboardData.panelists.find(p => p.id == selectedPanelist)?.username}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadPanelistSlots}
                disabled={loadingPanelistSlots}
                size="small"
              >
                Refresh
              </Button>
            </Box>

            {loadingPanelistSlots ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading slots...</Typography>
              </Box>
            ) : panelistSlots.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {panelistSlots.map((slot) => (
                      <TableRow key={slot.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {slot.start_datetime ? new Date(slot.start_datetime).toLocaleDateString() : 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {slot.start_datetime && slot.end_datetime ?
                                `${new Date(slot.start_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(slot.end_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                                : 'Time not available'
                              }
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={slot.start_datetime && slot.end_datetime ?
                              `${Math.round((new Date(slot.end_datetime) - new Date(slot.start_datetime)) / (1000 * 60))} mins`
                              : 'N/A'
                            }
                            size="small"
                            color="info"
                            icon={<AccessTime />}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={slot.slot_type || 'interview'}
                            size="small"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={slot.is_booked ? 'Booked' : slot.is_available ? 'Available' : 'Unavailable'}
                            color={slot.is_booked ? 'warning' : slot.is_available ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {slot.notes || 'No notes'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Delete Slot">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setConfirmDialog({
                                    open: true,
                                    title: 'Delete Slot',
                                    message: `Are you sure you want to delete this slot?`,
                                    action: () => handleDeleteSlot(slot.id)
                                  });
                                }}
                                disabled={slot.is_booked}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant='body1' color="text.secondary">
                  No slots found for this panelist
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setShowCreateSlotDialog(true)}
                  sx={{ mt: 2 }}

                >
                  Create First Slot
                </Button>
              </Box>
                       )}
          </Paper>
        )}

        {/* Original PanelistAvailabilityManagement component as fallback */}
        <PanelistAvailabilityManagement
          selectedPanelist={selectedPanelist}
          onPanelistChange={setSelectedPanelist}
          onSlotUpdate={loadAllSlotsForCalendar}
          panelists={dashboardData.panelists}
          userRole="admin"
        />

        {/* Create Slot Dialog */}
        <Dialog open={showCreateSlotDialog} onClose={() => setShowCreateSlotDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Add color="primary" />
              Create Slot for {dashboardData.panelists.find(p => p.id == selectedPanelist)?.username}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
              <TextField
                label="Date"
                type="date"
                value={newSlotData.date}
                onChange={(e) => setNewSlotData({ ...newSlotData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />

              <Stack direction="row" spacing={2}>
                <TimePickerButton
                  label="Start Time"
                  value={newSlotData.start_time}
                  onChange={(time) => setNewSlotData({ ...newSlotData, start_time: time })}
                  helperText="e.g., 9:00 AM"
                />

                <TimePickerButton
                  label="End Time"
                  value={newSlotData.end_time}
                  onChange={(time) => setNewSlotData({ ...newSlotData, end_time: time })}
                  helperText="e.g., 10:00 AM"
                />
              </Stack>

              <FormControl fullWidth>
                <InputLabel>Slot Type</InputLabel>
                <Select
                  value={newSlotData.slot_type}
                  onChange={(e) => setNewSlotData({ ...newSlotData, slot_type: e.target.value })}
                  label="Slot Type"
                >
                  <MenuItem value="interview">Interview</MenuItem>
                  <MenuItem value="cv_review">CV Review</MenuItem>
                  <MenuItem value="feedback">Feedback Session</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Notes"
                multiline
                rows={2}
                value={newSlotData.notes}
                onChange={(e) => setNewSlotData({ ...newSlotData, notes: e.target.value })}
                fullWidth
                placeholder="Optional notes for this slot..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateSlotDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreatePanelistSlot}
              variant="contained"
              startIcon={<Add />}
              disabled={!selectedPanelist || !newSlotData.date || !newSlotData.start_time || !newSlotData.end_time}
            >
              Create Slot
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

  // ADDED: Team Calendar Section
  const TeamCalendarSection = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SectionHeader
        title="Team Availability Calendar"
        subtitle={`${allSlots.length} slots across the team`}
        actions={
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadAllSlotsForCalendar}
            disabled={slotManagementLoading}
          >
            Refresh Calendar
          </Button>
        }
      />

        {/* Calendar Filters */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={slotFilters.status}
                  label="Filter by Status"
                  onChange={(e) => setSlotFilters({ ...slotFilters, status: e.target.value })}
                >
                  <MenuItem value="all">All Slots</MenuItem>
                  <MenuItem value="available">Available Only</MenuItem>
                  <MenuItem value="booked">Booked Only</MenuItem>
                  <MenuItem value="unavailable">Unavailable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Panelist</InputLabel>
                <Select
                  value={slotFilters.panelist}
                  label="Filter by Panelist"
                  onChange={(e) => setSlotFilters({ ...slotFilters, panelist: e.target.value })}
                >
                  <MenuItem value="all">All Panelists</MenuItem>
                  {dashboardData.panelists.filter(p => p.is_active).map(panelist => (
                    <MenuItem key={panelist.id} value={panelist.id}>
                      {panelist.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1} alignItems="center" justifyContent="space-between">
                <Typography variant="body2">
                  Showing {allSlots.length} slots from {dashboardData.panelists.filter(p => p.is_active).length} panelists
                </Typography>
                <Box display="flex" gap={1}>
                  <Chip
                    label={`${allSlots.filter(s => s.is_available && !s.is_booked).length} Available`}
                    color="success"
                    size="small"
                  />
                  <Chip
                    label={`${allSlots.filter(s => s.is_booked).length} Booked`}
                    color="error"
                    size="small"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => setSlotFilters({ status: 'all', panelist: 'all', dateRange: { start: null, end: null } })}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {slotManagementLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading team calendar...
            </Typography>
          </Box>
        ) : (
          <Box>
            <TalentFlowCalendar
              events={allSlots}
              userRole="admin"
              view="team"
              onEventSelect={(slot) => {
                console.log('Admin selected slot:', slot);
              }}
              height={600}
            />

            {/* Calendar Legend */}
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Calendar Legend
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#4caf50', borderRadius: '2px' }} />
                  <Typography variant="caption">Available Slots</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#f44336', borderRadius: '2px' }} />
                  <Typography variant="caption">Booked Slots</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#ff9800', borderRadius: '2px' }} />
                  <Typography variant="caption">Recurring Slots</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#9e9e9e', borderRadius: '2px' }} />
                  <Typography variant="caption">Unavailable</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
    </Box>
  );

  // ✅ UPDATED: Job Tracking Section Component
  const JobTrackingSection = () => {
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [showOverview, setShowOverview] = useState(true);

    return (
        <Stack spacing={3}>
            <SectionHeader
                title="Job Tracking"
                subtitle="Pipeline progress across every open job"
                actions={
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant={showOverview ? 'contained' : 'outlined'}
                            onClick={() => setShowOverview(true)}
                        >
                            Overview
                        </Button>
                        <Button
                            variant={!showOverview ? 'contained' : 'outlined'}
                            onClick={() => setShowOverview(false)}
                        >
                            Detailed View
                        </Button>
                    </Stack>
                }
            />

            {!showOverview && (
                <Card>
                    <CardContent>
                        <FormControl fullWidth>
                            <InputLabel>Select a Job to Track</InputLabel>
                            <Select
                                value={selectedJobId || ''}
                                label="Select a Job to Track"
                                onChange={(e) => setSelectedJobId(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Select a job...</em>
                                </MenuItem>
                                {dashboardData.jobs.map(job => (
                                    <MenuItem key={job.id} value={job.id}>
                                        {job.title} ({job.job_code || `JOB-${job.id}`})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </CardContent>
                </Card>
            )}

            <JobTrackingPipeline
                jobId={selectedJobId}
                showOverview={showOverview}
            />
        </Stack>
    );
  };


  // PRESERVED: Mount/unmount and load effects exactly as they were
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      loadingRef.current = false;
      hasLoadedRef.current = false;
    };
  }, []);

  // ADDED: Load slots when panelists data is available
  useEffect(() => {
    if (dashboardData.panelists.length > 0 && (tabValue === 4 || tabValue === 5)) {
      loadAllSlotsForCalendar();
    }
  }, [dashboardData.panelists, tabValue, loadAllSlotsForCalendar]);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin' && mountedRef.current && !hasLoadedRef.current && !loadingRef.current) {
      console.log('ðŸš€ Triggering dashboard load (SINGLE TIME)...');
      loadDashboardData();
      fetchRoleAssignments(); // Load role assignments on initial load
    }
  }, [isAuthenticated, user?.role, loadDashboardData, fetchRoleAssignments]);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}>
        <Box sx={{ 
          textAlign: 'center',
          p: 4,
          borderRadius: 3,
          bgcolor: 'white',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
        }}>
          <CircularProgress size={48} sx={{ mb: 3, color: tokens.color.accent }} />
          <Typography variant="h6">Loading your dashboard</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: tokens.color.inkSecondary }}>
            Fetching jobs, users and interview data
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Not authenticated - redirecting to login...
        </Typography>
        <CircularProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Access denied - admin role required
        </Typography>
      </Box>
    );
  }

  return (
    <AppShell
      role="admin"
      sections={tabsConfig}
      activeIndex={tabValue}
      onSectionChange={changeTabTo}
      onRefresh={handleManualRefresh}
      loading={dashboardData.loading}
      error={dashboardData.error}
      actions={[
        {
          label: 'Create Schedule',
          icon: <Repeat />,
          onClick: () => setAdvancedSlotDialog(true),
          variant: 'contained',
          tooltip: 'Create recurring slot schedule',
        },
      ]}
    >
      <Box>
          <Box>

        {/* PRESERVED: Overview Tab with added slot stats */}
        <TabPanel value={tabValue} index={0}>
          {dashboardData.analytics && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Jobs"
                  value={dashboardData.analytics.total_jobs || 0}
                  icon={<Work />}
                  color="#5c6ac4"
                  subtitle="All job postings"
                  onClick={() => setTabValue(1)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Users"
                  value={(dashboardData.vendors.length
                          + dashboardData.panelists.length
                          + dashboardData.managers.length) || 0}
                  icon={<People />}
                  color="#ff9800"
                  subtitle="Managers, vendors & panelists"
                  onClick={() => setTabValue(2)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Available Slots"
                  value={allSlots.filter(s => s.is_available && !s.is_booked).length}
                  icon={<CalendarIcon />}
                  color="#4caf50"
                  subtitle="Ready for booking"
                  onClick={() => setTabValue(4)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Team Slots"
                  value={allSlots.length}
                  icon={<People />}
                  color="#2196f3"
                  subtitle="All panelist slots"
                  onClick={() => setTabValue(5)}
                />
              </Grid>
            </Grid>
          )}

          <Card sx={{ 
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.05)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box sx={{ 
                  width: 8, 
                  height: 24, 
                  bgcolor: '#667eea', 
                  borderRadius: 1 
                }} />
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setJobDialog(true)}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#667eea',
                    color: '#667eea',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#667eea',
                      backgroundColor: '#667eea',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                    }
                  }}
                >
                  Create Job
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Today />}
                  onClick={() => setSlotDialog(true)}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#4caf50',
                    color: '#4caf50',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#4caf50',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
                    }
                  }}
                >
                  Single Slot
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Repeat />}
                  onClick={() => setAdvancedSlotDialog(true)}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#ff9800',
                    color: '#ff9800',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#ff9800',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(255, 152, 0, 0.3)'
                    }
                  }}
                >
                  Weekly Schedule
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ManageAccounts />}
                  onClick={() => setUserDialog(true)}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#9c27b0',
                    color: '#9c27b0',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#9c27b0',
                      backgroundColor: '#9c27b0',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(156, 39, 176, 0.3)'
                    }
                  }}
                >
                  Add User
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<VideoCall />}
                  onClick={() => setTabValue(3)}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#f44336',
                    color: '#f44336',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#f44336',
                      backgroundColor: '#f44336',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(244, 67, 54, 0.3)'
                    }
                  }}
                >
                  View Interviews
                </Button>
                {/* Enhanced slot management actions */}
                <Button
                  variant="outlined"
                  startIcon={<CalendarIcon />}
                  onClick={() => setTabValue(4)}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#00bcd4',
                    color: '#00bcd4',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#00bcd4',
                      backgroundColor: '#00bcd4',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(0, 188, 212, 0.3)'
                    }
                  }}
                >
                  Manage Slots
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Schedule />}
                  onClick={() => setTabValue(4)}
                  sx={{
                    borderRadius: 2,
                    borderColor: '#4caf50',
                    color: '#4caf50',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#4caf50',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
                    }
                  }}
                >
                  Team Calendar
                </Button>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>

       {/* ✅ FIND AND REPLACE this TabPanel */}
<TabPanel value={tabValue} index={1}>
  <JobsManagementSection />
</TabPanel>


        <TabPanel value={tabValue} index={2}>
          {/*
            Called as a function, not rendered as <UsersManagementSection />.
            It is declared inside AdminDashboard, so as an element its component
            type changes on every render and React remounts the entire section --
            discarding the DOM node any open menu is anchored to, along with
            scroll position and focus. Invoking it inlines the JSX into this
            component's own tree, so the rows persist across renders. It uses no
            hooks of its own, so calling it directly is safe.

            Guarded on the active tab because a call, unlike an element, runs
            during this component's render whether or not TabPanel goes on to
            display it.
          */}
          {tabValue === 2 && UsersManagementSection()}
        </TabPanel>


        <TabPanel value={tabValue} index={3}>
          <InterviewManagement />
        </TabPanel>

        {/* ADDED: New slot management tabs */}
        <TabPanel value={tabValue} index={4}>
          <SlotManagementSection />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <TeamCalendarSection />
        </TabPanel>

        {/* Job Tracking Tab */}
        <TabPanel value={tabValue} index={6}>
          <JobTrackingSection />
        </TabPanel>

        {/* Add the new Candidate Search tab panel */}
        <TabPanel value={tabValue} index={7}>
          <CandidateSearch />
        </TabPanel>
        <TabPanel value={tabValue} index={8}>
          <CandidateReviews />
        </TabPanel>
        <TabPanel value={tabValue} index={9}>
          <InterviewSearch />
        </TabPanel>

          </Box>
        </Box>

      {/* ALL YOUR EXISTING DIALOGS - PRESERVED EXACTLY AS THEY WERE */}

      {/* Single Slot Creation Dialog */}
      <Dialog open={slotDialog} onClose={() => setSlotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Today color="primary" />
            Create Single Slot
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Panelist</InputLabel>
              <Select
                value={newSlot.panelist_id}
                onChange={(e) => setNewSlot({ ...newSlot, panelist_id: e.target.value })}
                label="Select Panelist"
              >
                {dashboardData.panelists.map((panelist) => (
                  <MenuItem key={panelist.id} value={panelist.id}>
                    <Box>
                      <Typography variant="subtitle2">{panelist.username}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Date"
              type="date"
              value={newSlot.date}
              onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              helperText="Select the specific date for this slot"
            />

            <Stack direction="row" spacing={2}>
              <TimePickerButton
                label="Start Time"
                value={newSlot.start_time}
                onChange={(time) => setNewSlot({ ...newSlot, start_time: time })}
                helperText="e.g., 3:00 PM"
              />

              <TimePickerButton
                label="End Time"
                value={newSlot.end_time}
                onChange={(time) => setNewSlot({ ...newSlot, end_time: time })}
                helperText="e.g., 4:00 PM"
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel>Slot Type</InputLabel>
              <Select
                value={newSlot.slot_type}
                onChange={(e) => setNewSlot({ ...newSlot, slot_type: e.target.value })}
                label="Slot Type"
              >
                <MenuItem value="interview">Interview</MenuItem>
                <MenuItem value="cv_review">CV Review</MenuItem>
                <MenuItem value="feedback">Feedback Session</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Notes"
              multiline
              rows={2}
              value={newSlot.notes}
              onChange={(e) => setNewSlot({ ...newSlot, notes: e.target.value })}
              fullWidth
              placeholder="Optional notes for this slot..."
            />

            {newSlot.start_time && newSlot.end_time && newSlot.date && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>ðŸ”  Debug Info:</strong>
                  <br />Date: {newSlot.date}
                  <br />Start: {newSlot.start_time}
                  <br />End: {newSlot.end_time}
                  <br />Combined From: {combineDateTime(newSlot.date, newSlot.start_time)}
                  <br />Combined To: {combineDateTime(newSlot.date, newSlot.end_time)}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlotDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSlot}
            variant="contained"
            startIcon={<Today />}
            disabled={!newSlot.panelist_id || !newSlot.date || !newSlot.start_time || !newSlot.end_time}
          >
            Create Single Slot
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advanced Weekly Schedule Dialog */}
      <Dialog open={advancedSlotDialog} onClose={() => setAdvancedSlotDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Repeat color="primary" />
            Create Weekly Schedule
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Panelist</InputLabel>
              <Select
                value={advancedSlot.panelist_id}
                onChange={(e) => setAdvancedSlot({ ...advancedSlot, panelist_id: e.target.value })}
                label="Select Panelist"
              >
                {dashboardData.panelists.map((panelist) => (
                  <MenuItem key={panelist.id} value={panelist.id}>
                    <Box>
                      <Typography variant="subtitle2">{panelist.username}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {panelist.expertise} â€¢ Department: {panelist.department}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={advancedSlot.is_recurring}
                  onChange={(e) => setAdvancedSlot({ ...advancedSlot, is_recurring: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Create Recurring Schedule</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Toggle to create multiple slots across weeks
                  </Typography>
                </Box>
              }
            />

            {advancedSlot.is_recurring && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Schedule Pattern</InputLabel>
                  <Select
                    value={advancedSlot.recurrence_pattern}
                    onChange={(e) => setAdvancedSlot({ ...advancedSlot, recurrence_pattern: e.target.value })}
                    label="Schedule Pattern"
                  >
                    <MenuItem value="weekly">Weekly (Same days each week)</MenuItem>
                    <MenuItem value="daily">Daily (Every day)</MenuItem>
                  </Select>
                </FormControl>

                {advancedSlot.recurrence_pattern === 'weekly' && (
                  <Box>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Select Days of Week:
                    </FormLabel>
                    <FormGroup row>
                      {[
                        { key: 'mon', label: 'Monday' },
                        { key: 'tue', label: 'Tuesday' },
                        { key: 'wed', label: 'Wednesday' },
                        { key: 'thu', label: 'Thursday' },
                        { key: 'fri', label: 'Friday' },
                        { key: 'sat', label: 'Saturday' },
                        { key: 'sun', label: 'Sunday' }
                      ].map((day) => (
                        <FormControlLabel
                          key={day.key}
                          control={
                            <Checkbox
                              checked={advancedSlot.recurrence_days.includes(day.key)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAdvancedSlot({
                                    ...advancedSlot,
                                    recurrence_days: [...advancedSlot.recurrence_days, day.key]
                                  });
                                } else {
                                  setAdvancedSlot({
                                    ...advancedSlot,
                                    recurrence_days: advancedSlot.recurrence_days.filter(d => d !== day.key)
                                  });
                                }
                              }}
                            />
                          }
                          label={day.label}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}

                <TextField
                  label="Schedule End Date (Optional)"
                  type="date"
                  value={advancedSlot.recurrence_end_date}
                  onChange={(e) => setAdvancedSlot({ ...advancedSlot, recurrence_end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  helperText="Leave empty to create 8 weeks of slots"
                />
              </>
            )}

            <Divider>
              <Chip label="Time Settings" size="small" />
            </Divider>

            {!advancedSlot.is_recurring && (
              <TextField
                label="Date"
                type="date"
                value={advancedSlot.date}
                onChange={(e) => setAdvancedSlot({ ...advancedSlot, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                helperText="Select the date for this single slot"
              />
            )}

            <Stack direction="row" spacing={2}>
              <TimePickerButton
                label="Start Time"
                value={advancedSlot.start_time}
                onChange={(time) => handleAdvancedSlotTimeChange('start_time', time)}
                helperText="Time for all selected days"
                fullWidth={true}
              />

              <TimePickerButton
                label="End Time"
                value={advancedSlot.end_time}
                onChange={(time) => handleAdvancedSlotTimeChange('end_time', time)}
                helperText="Time for all selected days"
                fullWidth={true}
              />
            </Stack>

            <FormControl fullWidth>
              <InputLabel>Slot Type</InputLabel>
              <Select
                value={advancedSlot.slot_type}
                onChange={(e) => setAdvancedSlot({ ...advancedSlot, slot_type: e.target.value })}
                label="Slot Type"
              >
                <MenuItem value="interview">Interview</MenuItem>
                <MenuItem value="cv_review">CV Review</MenuItem>
                <MenuItem value="feedback">Feedback Session</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Schedule Notes"
              multiline
              rows={2}
              value={advancedSlot.notes}
              onChange={(e) => setAdvancedSlot({ ...advancedSlot, notes: e.target.value })}
              fullWidth
              placeholder="Notes for this schedule..."
            />

            {/* Enhanced Preview */}
            {advancedSlot.is_recurring && advancedSlot.recurrence_days.length > 0 && advancedSlot.start_time && advancedSlot.end_time && (
              <Alert severity="success" icon={<Schedule />}>
                <Typography variant="body2">
                  <strong>ðŸ“… Will create slots for:</strong> {advancedSlot.recurrence_days.join(', ').toUpperCase()}
                  <br />
                  <strong>ðŸ”„ Pattern:</strong> {advancedSlot.recurrence_pattern}
                  <br />
                  <strong>â ° Time:</strong> {new Date(`2000-01-01 ${advancedSlot.start_time}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true})} - {new Date(`2000-01-01 ${advancedSlot.end_time}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true})}
                  <br />
                  <strong>ðŸ”  Debug:</strong> Days={JSON.stringify(advancedSlot.recurrence_days)}
                </Typography>
              </Alert>
            )}

            {!advancedSlot.is_recurring && advancedSlot.start_time && advancedSlot.end_time && (
                           <Alert severity="info" icon={<Today />}>
                <Typography variant="body2">
                  <strong>ðŸ“… Single slot:</strong> {advancedSlot.date}
                  <br />
                  <strong>â ° Time:</strong> {advancedSlot.start_time && new Date(`2000-01-01 ${advancedSlot.start_time}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true})} - {advancedSlot.end_time && new Date(`2000-01-01 ${advancedSlot.end_time}`).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', hour12: true})}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvancedSlotDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateAdvancedSlot}
            variant="contained"
            startIcon={<Repeat />}
            disabled={
              !advancedSlot.panelist_id ||
              !advancedSlot.start_time ||
              !advancedSlot.end_time ||
              (advancedSlot.is_recurring ? advancedSlot.recurrence_days.length === 0 : !advancedSlot.date)
            }
          >
            {advancedSlot.is_recurring ? 'Create Schedule' : 'Create Single Slot'}
          </Button>
        </DialogActions>
      </Dialog>



{/* ✅ JOB CREATION DIALOG - ADD THIS */}
<Dialog open={jobDialog} onClose={() => setJobDialog(false)} maxWidth="md" fullWidth>
  <DialogTitle>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Add color="primary" />
      Create New Job Opportunity
    </Box>
  </DialogTitle>

  <DialogContent>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      {/* Basic Fields */}
      <TextField
        fullWidth
        label="Job Title *"
        value={newJob.title}
        onChange={(e) => setNewJob({...newJob, title: e.target.value})}
        required
      />

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description *"
        value={newJob.description}
        onChange={(e) => setNewJob({...newJob, description: e.target.value})}
        required
      />

      <TextField
        fullWidth
        multiline
        rows={2}
        label="Requirements"
        value={newJob.requirements}
        onChange={(e) => setNewJob({...newJob, requirements: e.target.value})}
      />

      <TextField
        fullWidth
        label="Location *"
        value={newJob.location}
        onChange={(e) => setNewJob({...newJob, location: e.target.value})}
        required
      />

      {/* ✅ CURRENCY SECTION */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        💰 Budget Information
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={newJob.budget_currency}
              onChange={(e) => setNewJob({...newJob, budget_currency: e.target.value})}
              label="Currency"
            >
              {supportedCurrencies.map((currency) => (
                <MenuItem key={currency.code} value={currency.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {currency.symbol}
                    </Typography>
                    {currency.code} - {currency.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Minimum Budget"
            value={newJob.budget_min}
            onChange={(e) => setNewJob({...newJob, budget_min: e.target.value})}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: 'text.secondary' }}>
                  {supportedCurrencies.find(c => c.code === newJob.budget_currency)?.symbol || '$'}
                </Box>
              )
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Maximum Budget"
            value={newJob.budget_max}
            onChange={(e) => setNewJob({...newJob, budget_max: e.target.value})}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: 'text.secondary' }}>
                  {supportedCurrencies.find(c => c.code === newJob.budget_currency)?.symbol || '$'}
                </Box>
              )
            }}
          />
        </Grid>
      </Grid>

      {/* Legacy Salary Range Field */}
      <TextField
        fullWidth
        label="Salary Range (Legacy)"
        value={newJob.salary_range}
        onChange={(e) => setNewJob({...newJob, salary_range: e.target.value})}
        helperText="Optional: Traditional salary range text"
      />

      {/* Other Fields */}
      <TextField
        fullWidth
      />

      <FormControl fullWidth>
        <InputLabel>Hiring Urgency</InputLabel>
        <Select
          value={newJob.hiring_urgency}
          onChange={(e) => setNewJob({...newJob, hiring_urgency: e.target.value})}
          label="Hiring Urgency"
        >
          <MenuItem value="standard">📅 Standard</MenuItem>
          <MenuItem value="urgent">🚨 Urgent</MenuItem>
          <MenuItem value="flexible">⏰ Flexible</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        type="date"
        label="Expected Start Date"
        value={newJob.expected_start_date}
        onChange={(e) => setNewJob({...newJob, expected_start_date: e.target.value})}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        fullWidth
        type="date"
        label="Application Deadline"
        value={newJob.deadline}
        onChange={(e) => setNewJob({...newJob, deadline: e.target.value})}
        InputLabelProps={{ shrink: true }}
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={newJob.priority}
              onChange={(e) => setNewJob({...newJob, priority: e.target.value})}
              label="Priority"
            >
              <MenuItem value="low">🟢 Low</MenuItem>
              <MenuItem value="medium">🟡 Medium</MenuItem>
              <MenuItem value="high">🟠 High</MenuItem>
              <MenuItem value="urgent">🔴 Urgent</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {/* Add after your existing Priority field */}
<Grid item xs={12} sm={6}>
  <FormControl fullWidth margin="dense">
    <InputLabel>Sub-Priority</InputLabel>
    <Select
      value={newJob.sub_priority || ''}
      onChange={(e) => setNewJob({...newJob, sub_priority: e.target.value})}
      label="Sub-Priority"
    >
      <MenuItem value="">None</MenuItem>
      <MenuItem value="h1">H1 (Highest)</MenuItem>
      <MenuItem value="h2">H2</MenuItem>
      <MenuItem value="h3">H3</MenuItem>
      <MenuItem value="m1">M1 (Highest)</MenuItem>
      <MenuItem value="m2">M2</MenuItem>
      <MenuItem value="m3">M3</MenuItem>
      <MenuItem value="l1">L1 (Highest)</MenuItem>
      <MenuItem value="l2">L2</MenuItem>
      <MenuItem value="l3">L3</MenuItem>
    </Select>
  </FormControl>
</Grid>

<Grid item xs={12}>
  <TextField
    fullWidth
    type="datetime-local"
    label="Application Deadline"
    value={newJob.deadline || ''}
    onChange={(e) => setNewJob({...newJob, deadline: e.target.value})}
    InputLabelProps={{ shrink: true }}
    margin="dense"
    helperText="⚠️ Job will automatically close when deadline passes"
    FormHelperTextProps={{ sx: { color: 'warning.main' } }}
  />
</Grid>


        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Expected Hires"
            value={newJob.expected_hires}
            onChange={(e) => setNewJob({...newJob, expected_hires: e.target.value})}
            inputProps={{ min: 1 }}
          />
        </Grid>
      </Grid>

      <FormControlLabel
        control={
          <Switch
            checked={newJob.is_readonly}
            onChange={(e) => setNewJob({...newJob, is_readonly: e.target.checked})}
          />
        }
        label="Read-only (prevents modifications after creation)"
      />
    </Box>
  </DialogContent>

  <DialogActions sx={{ p: 2 }}>
    <Button onClick={() => setJobDialog(false)}>
      Cancel
    </Button>
    <Button
      onClick={handleCreateJob}
      variant="contained"
      startIcon={<Add />}
      disabled={!newJob.title || !newJob.description || !newJob.location}
    >
      Create Job
    </Button>
  </DialogActions>
</Dialog>


{/* ✅ EDIT JOB DIALOG - OLD STYLE WITH CURRENCY */}
<Dialog
  open={editJobDialog}
  onClose={() => setEditJobDialog(false)}
  maxWidth="lg"
  fullWidth
  PaperProps={{
    elevation: 3,
    sx: { 
      borderRadius: 2,
      maxHeight: '85vh',
      overflow: 'auto'
    }
  }}
>
  <DialogTitle sx={{
    pb: 1,
    pt: 1.5,
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#1976d2',
    display: 'flex',
    alignItems: 'center',
    gap: 1
  }}>
    <Edit color="primary" />
    Edit Job
  </DialogTitle>

  <DialogContent dividers sx={{ pt: 1.5, pb: 1.5 }}>
    <Box component="form" sx={{ mt: 1 }}>
      <Grid container spacing={1.5}>
        {/* Job Title */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Job Title *"
            value={editJob.title}
            onChange={(e) => setEditJob({...editJob, title: e.target.value})}
            required
            margin="dense"
          />
        </Grid>

        {/* Job Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Job Description"
            value={editJob.description}
            onChange={(e) => setEditJob({...editJob, description: e.target.value})}
            margin="dense"
          />
        </Grid>

        {/* Requirements */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Requirements"
            value={editJob.requirements}
            onChange={(e) => setEditJob({...editJob, requirements: e.target.value})}
            margin="dense"
          />
        </Grid>

        {/* Location */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Location"
            value={editJob.location}
            onChange={(e) => setEditJob({...editJob, location: e.target.value})}
            placeholder="e.g., New York, NY or Remote"
            margin="dense"
          />
        </Grid>

        {/* Priority */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              value={editJob.priority}
              onChange={(e) => setEditJob({...editJob, priority: e.target.value})}
              label="Priority"
            >
              <MenuItem value="low">🟢 Low</MenuItem>
              <MenuItem value="medium">🟡 Medium</MenuItem>
              <MenuItem value="high">🟠 High</MenuItem>
              <MenuItem value="urgent">🔴 Urgent</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Currency Selection */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Currency</InputLabel>
            <Select
              value={editJob.budget_currency}
              onChange={(e) => setEditJob({...editJob, budget_currency: e.target.value})}
              label="Currency"
            >
              {supportedCurrencies.map(currency => (
                <MenuItem key={currency.code} value={currency.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{currency.flag}</span>
                    <span>{currency.symbol} {currency.name}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Budget Min */}
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Minimum Budget"
            value={editJob.budget_min}
            onChange={(e) => setEditJob({...editJob, budget_min: e.target.value})}
            placeholder="50000"
            margin="dense"
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: 'text.secondary' }}>
                  {supportedCurrencies.find(c => c.code === editJob.budget_currency)?.symbol || '$'}
                </Box>
              ),
            }}
          />
        </Grid>

        {/* Budget Max */}
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Maximum Budget"
            value={editJob.budget_max}
            onChange={(e) => setEditJob({...editJob, budget_max: e.target.value})}
            placeholder="80000"
            margin="dense"
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, color: 'text.secondary' }}>
                  {supportedCurrencies.find(c => c.code === editJob.budget_currency)?.symbol || '$'}
                </Box>
              ),
            }}
          />
        </Grid>

        {/* Legacy Salary Range (for backward compatibility) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Salary Range (Legacy)"
            value={editJob.salary_range}
            onChange={(e) => setEditJob({...editJob, salary_range: e.target.value})}
            placeholder="Optional: Traditional salary range text"
            margin="dense"
            helperText="Optional: Use for traditional salary descriptions"
          />
        </Grid>

        {/* Hiring Urgency */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Hiring Urgency</InputLabel>
            <Select
              value={editJob.hiring_urgency}
              onChange={(e) => setEditJob({...editJob, hiring_urgency: e.target.value})}
              label="Hiring Urgency"
            >
              <MenuItem value="standard">📅 Standard</MenuItem>
              <MenuItem value="urgent">🚨 Urgent</MenuItem>
              <MenuItem value="flexible">⏰ Flexible</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Status */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="dense" size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={editJob.status}
              onChange={(e) => setEditJob({...editJob, status: e.target.value})}
              label="Status"
              sx={{
                minHeight: '40px',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '0.875rem',
                  fontWeight: 500
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fafafa'
                }
              }}
              renderValue={(selected) => {
                const statusConfig = {
                  'open': { color: '#2e7d32', emoji: '🟢', text: 'Open' },
                  'closed': { color: '#d32f2f', emoji: '🔴', text: 'Closed' },
                  'on_hold': { color: '#ef6c00', emoji: '🟡', text: 'On Hold' },
                  'draft': { color: '#5e35b1', emoji: '📝', text: 'Draft' },
                  'filled': { color: '#1565c0', emoji: '✅', text: 'Filled' }
                };
                const config = statusConfig[selected];
                return config ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: config.color }} />
                    {config.emoji} {config.text}
                  </Box>
                ) : 'Select Status';
              }}
            >
              <MenuItem value="open">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2e7d32' }} />
                  🟢 Open
                </Box>
              </MenuItem>
              <MenuItem value="closed">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#d32f2f' }} />
                  🔴 Closed
                </Box>
              </MenuItem>
              <MenuItem value="on_hold">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef6c00' }} />
                  🟡 On Hold
                </Box>
              </MenuItem>
              <MenuItem value="draft">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#5e35b1' }} />
                  📝 Draft
                </Box>
              </MenuItem>
              <MenuItem value="filled">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1565c0' }} />
                  ✅ Filled
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Expected Start Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Expected Start Date"
            value={editJob.expected_start_date}
            onChange={(e) => setEditJob({...editJob, expected_start_date: e.target.value})}
            InputLabelProps={{ shrink: true }}
            margin="dense"
          />
        </Grid>

        {/* Budget Preview */}
        {(editJob.budget_min || editJob.budget_max) && (
          <Grid item xs={12}>
            <Box sx={{
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Budget Preview:
              </Typography>
              <Typography variant="h6" color="primary">
                {editJob.budget_min && editJob.budget_max
                  ? `${formatCurrency(editJob.budget_min, editJob.budget_currency)} - ${formatCurrency(editJob.budget_max, editJob.budget_currency)}`
                  : editJob.budget_min
                    ? `${formatCurrency(editJob.budget_min, editJob.budget_currency)}+`
                    : editJob.budget_max
                      ? `Up to ${formatCurrency(editJob.budget_max, editJob.budget_currency)}`
                      : 'No budget specified'
                }
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  </DialogContent>

  <DialogActions sx={{ p: 2, gap: 1 }}>
    <Button
      onClick={() => setEditJobDialog(false)}
      color="inherit"
    >
      Cancel
    </Button>
    <Button
      onClick={handleUpdateJob}
      variant="contained"
      startIcon={<Save />}
      disabled={!editJob.title.trim()}
    >
      Update Job
    </Button>
  </DialogActions>
</Dialog>


      {/* User Creation Dialog */}
      <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Username"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="manager">Hiring Manager</MenuItem>
                <MenuItem value="vendor">Vendor / HR</MenuItem>
                <MenuItem value="panelist">Panelist</MenuItem>
              </Select>
            </FormControl>

            {newUser.role === 'vendor' && (
              <>
                <TextField
                  label="Company Name"
                  value={newUser.company_name}
                  onChange={(e) => setNewUser({ ...newUser, company_name: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Contact Person"
                  value={newUser.contact_person}
                  onChange={(e) => setNewUser({ ...newUser, contact_person: e.target.value })}
                  fullWidth
                />
              </>
            )}

            {newUser.role === 'manager' && (
              // A manager has no side profile table — department lives on the
              // user row — so this is the only role-specific field they need.
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={newUser.department}
                  onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                  label="Department"
                >
                  <MenuItem value="Engineering">Engineering</MenuItem>
                  <MenuItem value="Product">Product</MenuItem>
                  <MenuItem value="Data">Data</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="Sales">Sales</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Operations">Operations</MenuItem>
                </Select>
              </FormControl>
            )}

            {newUser.role === 'panelist' && (
              <>
                <TextField
                  label="Expertise"
                  value={newUser.expertise}
                  onChange={(e) => setNewUser({ ...newUser, expertise: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Experience Years"
                  type="number"
                  value={newUser.experience_years}
                  onChange={(e) => setNewUser({ ...newUser, experience_years: parseInt(e.target.value) })}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={newUser.department}
                    onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                    label="Department"
                  >
                    <MenuItem value="HR">HR</MenuItem>
                    <MenuItem value="Engineering">Engineering</MenuItem>
                    <MenuItem value="Marketing">Marketing</MenuItem>
                    <MenuItem value="Sales">Sales</MenuItem>
                    <MenuItem value="Finance">Finance</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            <TextField
              label="Phone"
              value={newUser.phone}
              onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create User</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUserDialog} onClose={() => setEditUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Username"
              value={editUser.username}
              onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={editUser.email}
              onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
              fullWidth
              required
            />

            {editUser.role === 'vendor' && (
              <>
                <TextField
                  label="Company Name"
                  value={editUser.company_name}
                  onChange={(e) => setEditUser({ ...editUser, company_name: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Contact Person"
                  value={editUser.contact_person}
                  onChange={(e) => setEditUser({ ...editUser, contact_person: e.target.value })}
                  fullWidth
                />
              </>
            )}

            {editUser.role === 'panelist' && (
              <>
                <TextField
                  label="Expertise"
                  value={editUser.expertise}
                  onChange={(e) => setEditUser({ ...editUser, expertise: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Experience Years"
                  type="number"
                  value={editUser.experience_years}
                  onChange={(e) => setEditUser({ ...editUser, experience_years: parseInt(e.target.value) })}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={editUser.department}
                    onChange={(e) => setEditUser({ ...editUser, department: e.target.value })}
                    label="Department"
                  >
                    <MenuItem value="HR">HR</MenuItem>
                    <MenuItem value="Engineering">Engineering</MenuItem>
                    <MenuItem value="Marketing">Marketing</MenuItem>
                    <MenuItem value="Sales">Sales</MenuItem>
                    <MenuItem value="Finance">Finance</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            <TextField
              label="Phone"
              value={editUser.phone}
              onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">Update User</Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog 
        open={assignmentDialog} 
        onClose={() => setAssignmentDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            maxWidth: '900px',
            margin: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Assignment color="primary" />
            <Typography variant="h6" component="span">
              Manage Assignments - {selectedJob?.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Assign vendors and panelists to this job. You can add multiple vendors and panelists.
            </Typography>
          </Box>
          <Grid container spacing={3} justifyContent="center">
            {/* Vendors Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                bgcolor: 'background.default', 
                p: 2.5, 
                borderRadius: 2, 
                border: 1, 
                borderColor: 'divider', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                minHeight: '400px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                },
                transition: 'box-shadow 0.3s ease'
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 2.5, 
                  fontWeight: 600, 
                  color: 'primary.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: 1,
                  pb: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  width: '100%'
                }}>
                  <BusinessCenter fontSize="small" />
                  Vendors
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Available to Assign:
                  </Typography>
                  {dashboardData.vendors
                    .filter(vendor => !assignmentData.currentVendors.some(cv => cv.id === vendor.id))
                    .map((vendor) => (
                      <Box
                        key={vendor.id}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderRadius: 1.5,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          width: '100%',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                          '&:hover': { 
                            bgcolor: 'action.hover',
                            borderColor: 'primary.main',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {vendor.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {vendor.company_name}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleAssignVendor(vendor.id)}
                          startIcon={<Add />}
                          sx={{
                            minWidth: '80px',
                            fontWeight: 500,
                            textTransform: 'none',
                            fontSize: '0.875rem'
                          }}
                        >
                          Assign
                        </Button>
                      </Box>
                    ))}
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Currently Assigned:
                  </Typography>
                  {assignmentData.currentVendors.map((vendor) => (
                    <Box
                      key={vendor.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRadius: 1.5,
                        bgcolor: 'success.light',
                        border: 1,
                        borderColor: 'success.main',
                        width: '100%',
                        boxShadow: '0 1px 3px rgba(76, 175, 80, 0.15)',
                        '&:hover': {
                          bgcolor: 'success.main',
                          '& .MuiTypography-root': {
                            color: 'white'
                          }
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: 'left' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                          {vendor.username}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'success.main', fontSize: '0.875rem' }}>
                          {vendor.company_name}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => handleRemoveAssignment(assignmentData.jobId, vendor.id, 'vendor')}
                        startIcon={<Delete />}
                        sx={{
                          minWidth: '80px',
                          fontWeight: 500,
                          textTransform: 'none',
                          fontSize: '0.875rem'
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Panelists Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                bgcolor: 'background.default', 
                p: 2.5, 
                borderRadius: 2, 
                border: 1, 
                borderColor: 'divider', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                minHeight: '400px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                },
                transition: 'box-shadow 0.3s ease'
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 2.5, 
                  fontWeight: 600, 
                  color: 'info.main', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: 1,
                  pb: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  width: '100%'
                }}>
                  <People fontSize="small" />
                  Panelists
                </Typography>
                
                <Box sx={{ mb: 3, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="subtitle1" gutterBottom color="text.primary" sx={{ 
                    textAlign: 'center', 
                    fontWeight: 600,
                    mb: 1.5,
                    color: 'primary.dark'
                  }}>
                    Available to Assign:
                  </Typography>
                  {dashboardData.panelists
                    .filter(panelist => !assignmentData.currentPanelists.some(cp => cp.id === panelist.id))
                    .map((panelist) => (
                      <Box
                        key={panelist.id}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'action.hover' },
                          width: '100%',
                          gap: 2
                        }}
                      >
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {panelist.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {panelist.expertise}
                          </Typography>
                        </Box>
                        <Button
                          size="medium"
                          variant="contained"
                          color="info"
                          onClick={() => handleAssignPanelist(panelist.id)}
                          startIcon={<Add />}
                          sx={{
                            minWidth: '100px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2
                          }}
                        >
                          Assign
                        </Button>
                      </Box>
                    ))}
                </Box>

                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="subtitle1" gutterBottom color="text.primary" sx={{ 
                    textAlign: 'center',
                    fontWeight: 600,
                    mb: 1.5,
                    color: 'success.dark'
                  }}>
                    Currently Assigned:
                  </Typography>
                  {assignmentData.currentPanelists.map((panelist) => (
                    <Box
                      key={panelist.id}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRadius: 2,
                        bgcolor: 'success.light',
                        border: 2,
                        borderColor: 'success.main',
                        width: '100%',
                        boxShadow: '0 2px 6px rgba(76, 175, 80, 0.2)',
                        '&:hover': {
                          bgcolor: 'success.main',
                          '& .MuiTypography-root': {
                            color: 'white'
                          }
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: 'left' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                          {panelist.username}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'success.main', fontSize: '0.875rem' }}>
                          {panelist.expertise}
                        </Typography>
                      </Box>
                      <Button
                        size="medium"
                        variant="contained"
                        color="error"
                        onClick={() => handleRemoveAssignment(assignmentData.jobId, panelist.id, 'panelist')}
                        startIcon={<Delete />}
                        sx={{
                          minWidth: '100px',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: 2
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: 1, 
          borderColor: 'divider', 
          p: 2, 
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}>
          <Button
            variant="contained"
            size="medium"
            onClick={() => setAssignmentDialog(false)}
            startIcon={<Close />}
            sx={{
              minWidth: '120px',
              fontWeight: 500,
              textTransform: 'none'
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADDED: Team Calendar Dialog */}
      <Dialog
        open={teamCalendarDialog}
        onClose={() => setTeamCalendarDialog(false)}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
              Team Calendar Overview
            </Typography>
            <IconButton onClick={() => setTeamCalendarDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TeamCalendarSection />
        </DialogContent>
      </Dialog>

        {/* 🔽 NEW: Assign Role Dialog */}
        <Dialog open={showAssignDialog} onClose={() => setShowAssignDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Assign New User Role</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField
                        label="Full Name"
                        value={newAssignment.full_name}
                        onChange={(e) => setNewAssignment({ ...newAssignment, full_name: e.target.value })}
                        fullWidth
                        required
                    />
                    <TextField
                        label="Email Address"
                        type="email"
                        value={newAssignment.email}
                        onChange={(e) => setNewAssignment({ ...newAssignment, email: e.target.value })}
                        fullWidth
                        required
                    />
                    <FormControl fullWidth>
                        <InputLabel>User Type</InputLabel>
                        <Select
                            value={newAssignment.user_type}
                            onChange={(e) => setNewAssignment({ ...newAssignment, user_type: e.target.value })}
                            label="User Type"
                        >
                            <MenuItem value="internal">Internal</MenuItem>
                            <MenuItem value="external">External</MenuItem>
                        </Select>
                    </FormControl>
                    {newAssignment.user_type === 'internal' ? (
                        <TextField
                            label="Department"
                            value={newAssignment.department}
                            onChange={(e) => setNewAssignment({ ...newAssignment, department: e.target.value })}
                            fullWidth
                        />
                    ) : (
                        <TextField
                            label="Company Name"
                            value={newAssignment.company_name}
                            onChange={(e) => setNewAssignment({ ...newAssignment, company_name: e.target.value })}
                            fullWidth
                        />
                    )}
                     <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={newAssignment.role}
                            onChange={(e) => setNewAssignment({ ...newAssignment, role: e.target.value })}
                            label="Role"
                        >
                            <MenuItem value="manager">Hiring Manager</MenuItem>
                            <MenuItem value="panelist">Panelist</MenuItem>
                            <MenuItem value="vendor">Vendor / HR</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Authentication Method</InputLabel>
                        <Select
                            value={newAssignment.auth_method}
                            onChange={(e) => setNewAssignment({ ...newAssignment, auth_method: e.target.value })}
                            label="Authentication Method"
                            disabled // Assuming this might be fixed or determined by backend
                        >
                            <MenuItem value="microsoft_sso">Microsoft SSO</MenuItem>
                            <MenuItem value="google_sso">Google SSO</MenuItem>
                            <MenuItem value="password">Password</MenuItem>
                        </Select>
                        <FormHelperText>SSO users will be created/updated on first login.</FormHelperText>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowAssignDialog(false)}>Cancel</Button>
                <Button onClick={assignRole} variant="contained">Assign Role</Button>
            </DialogActions>
        </Dialog>


      {/*
        Row actions menu.
        Anchored to the button that opened it. The anchor origins below were
        always correct -- the menu appeared at the top-left because the button
        itself was being unmounted by a re-render before MUI could measure it.
        See the note on UserRow.
      */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // The menu is short and sits inside a scrollable table; without this MUI
        // shifts the page to keep the scrollbar width, which nudges the row out
        // from under the cursor as the menu opens.
        disableScrollLock
        elevation={0}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              mt: 0.5,
              border: `1px solid ${tokens.color.border}`,
              borderRadius: `${tokens.radius.md}px`,
              boxShadow: tokens.shadow.overlay,
              '& .MuiMenuItem-root': {
                fontSize: '0.875rem',
                py: 0.9,
                gap: 0,
              },
              // A fixed icon column, so the three labels start on the same
              // x-position. They were inline `sx={{ mr: 1 }}` icons of differing
              // widths, which left the labels visibly ragged.
              '& .MuiListItemIcon-root': {
                minWidth: 32,
                '& .MuiSvgIcon-root': { fontSize: 18 },
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => handleEditUser(selectedUser)}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
            Edit user
          </ListItemText>
        </MenuItem>

        <MenuItem
          // Both the label and the action derive from isBlocked(), so they can
          // never disagree. Passing is_active directly here would mean an
          // undefined value reads as "not blocked" for the label but as
          // "currently inactive" for the action, offering to Block someone and
          // then unblocking them.
          onClick={() => handleToggleUserStatus(selectedUser?.id, !isBlocked(selectedUser))}
          sx={{ color: isBlocked(selectedUser) ? tokens.color.success : tokens.color.warning }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            {isBlocked(selectedUser) ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
            {isBlocked(selectedUser) ? 'Unblock user' : 'Block user'}
          </ListItemText>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => handleDeleteUser(selectedUser?.id)}
          sx={{ color: tokens.color.danger }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}><Delete fontSize="small" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
            Delete user
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, title: '', message: '', action: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.action) {
                confirmDialog.action();
              }
              setConfirmDialog({ open: false, title: '', message: '', action: null });
            }}
            variant="contained"
            color="error"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </AppShell>
  );
}

export default AdminDashboard;